import {
  EXPERIENCE_CONTRACT_UNKNOWN,
  normalizeExperienceContract,
  type ExperienceContract,
} from "@/lib/experience-contract";
import type { WalletActivity, WalletActivityEntry, WebhookEndpoint, WebhookDelivery } from "@/types";
import { extractSupportInfo } from "@/i18n/support-reference";
import { categoryForReasonCode } from "@/i18n/error-categories";
import {
  STEP_UP_RESUME_FALLBACK_TTL_MS,
  clearPendingStepUpResume,
  isSafeReturnPath,
  redirectToStepUpLogin,
  savePendingStepUpResume,
  type PendingStepUpResume,
} from "@/lib/step-up-resume";
import { reportUnauthorized } from "@/lib/session-expiry";
import { reportForbidden } from "@/lib/terms-gate";

const CSRF_COOKIE = "__Host-alloy_csrf";

function getCsrfToken(): string | null {
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${CSRF_COOKIE}=`));
  return match ? decodeURIComponent(match.split("=")[1]) : null;
}

function correlationId(scope = "api"): string {
  // Must satisfy the gateway contract ^[A-Za-z0-9][A-Za-z0-9._:-]{7,95}$ — note
  // that underscores are NOT in that set — and must be unique per request.
  // The previous form was `corr_sandbox_ui_${scope}_${Date.now()}`: parallel
  // calls issued in the same millisecond (the approvals page fires four at
  // once) produced identical ids, and the gateway rejected the duplicates with
  // 401 AUTH_CONTEXT_INVALID. Which of the four failed varied per page load.
  const safeScope = scope.replace(/[^A-Za-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "api";
  const unique = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `corr.sandbox.ui.${safeScope}.${unique}`.slice(0, 96);
}

/**
 * Gateway mutation routes that carry a `GatewayIdempotencyEnvelopeFilter`
 * (POST /v1/walletkit/wallets, POST /v1/transfers, /v1/transfers/estimate,
 * /v1/transfers/drafts, the draft submit/PUT/DELETE paths, approvals
 * approve/reject, ...) require an `Idempotency-Key` header matching
 * `^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$` or they 400 with VALIDATION_ERROR before
 * any auth/business check runs. Underscores are NOT in that set: the previous
 * `idem_sandbox_ui_<scope>_...` form was rejected on every call (ALL-4839).
 * Each call gets its own key so a retry after a network error is never
 * silently deduped against an earlier attempt's key.
 */
export function idempotencyKey(scope = "op"): string {
  const safeScope = scope.replace(/[^A-Za-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 48) || "op";
  // CSPRNG entropy (a v4 UUID is 36 chars of [0-9a-f-]); the key is a
  // deduplication envelope, never key material.
  const unique = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  return `idem.sandbox.ui.${safeScope}.${unique}`;
}

interface ApiFetchResult {
  response: Response;
  correlationId: string;
}

async function apiFetchWithCorrelation(
  path: string,
  options: RequestInit = {},
  scope?: string,
): Promise<ApiFetchResult> {
  const requestCorrelationId = correlationId(scope);
  const headers = new Headers(options.headers);
  headers.set("accept", "application/json");
  headers.set("x-alloy-correlation-id", requestCorrelationId);

  const csrf = getCsrfToken();
  if (csrf && options.method && options.method !== "GET") {
    headers.set("x-alloy-csrf-token", csrf);
  }

  const response = await fetch(path, {
    ...options,
    cache: "no-store" as RequestCache,
    headers,
    credentials: "same-origin",
  });
  // ALL-5718 (IAM-134): a 401 asks GET /auth/session whether the browser
  // session ended; only a confirmed end shows the session-ended state and
  // redirects to sign-in. The caller still gets this response unchanged.
  if (response.status === 401) void reportUnauthorized(path, response.status);
  // ALL-5722 (specs/terms-acceptance-gate S-10): a 403 AUTH_TERMS_ACCEPTANCE_REQUIRED
  // mid-session (a new terms version was published) re-runs the session check so
  // the terms page replaces the app. Reads a clone; the caller's body is untouched.
  if (response.status === 403) void reportForbidden(path, response);
  return { response, correlationId: requestCorrelationId };
}

export async function apiFetch(
  path: string,
  options: RequestInit = {},
  scope?: string,
): Promise<Response> {
  const { response } = await apiFetchWithCorrelation(path, options, scope);
  return response;
}

// --- Auth ---

export interface SessionInfo {
  authenticated: boolean;
  email?: string;
  /** Person name from the identity provider; absent for accounts that never supplied one. */
  displayName?: string;
  roleNames?: string[];
  /**
   * True only when `/auth/session` itself could not be answered (network
   * failure or a 5xx from the gateway) — distinct from a clean, confirmed
   * "not authenticated" response. Callers must render an explicit error
   * state on `transportError`, never fall through to a logged-out or
   * fixture-backed shell as if the check had succeeded.
   */
  transportError?: boolean;
  /** ALL-5722: true while this person has not accepted the current terms version. */
  termsAcceptanceRequired?: boolean;
  /** ALL-5722: the version to accept; present when termsAcceptanceRequired. */
  termsVersion?: string;
  /**
   * ALL-5747 (specs/alloy-mfa S-2): the session's Alloy-native 2FA state —
   * NOT_REQUIRED, ENROLMENT_SUGGESTED, ENROLMENT_REQUIRED, CHALLENGE_REQUIRED or VERIFIED.
   */
  mfaState?: string;
  /** ALL-5747: the methods of the principal's active factors ("passkey", "totp"). */
  mfaMethodsEnrolled?: string[];
  /** The identity provider the session signed in with (e.g. "google"). */
  identityProviderId?: string;
  [key: string]: unknown;
}

export async function checkSession(): Promise<SessionInfo> {
  let res: Response;
  try {
    res = await apiFetch("/auth/session", {}, "session");
  } catch {
    return { authenticated: false, transportError: true };
  }
  if (res.status >= 500) return { authenticated: false, transportError: true };
  if (!res.ok) return { authenticated: false };
  try {
    const data = await res.json();
    if (!data || !data.authenticated) return { authenticated: false };
    return data as SessionInfo;
  } catch {
    return { authenticated: false, transportError: true };
  }
}

export interface AuthMethod {
  id: string;
  label: string;
  detail: string;
  startUrl: string;
  enabled: boolean;
  primary: boolean;
}

function safeSameOriginPath(value: unknown): string {
  if (typeof value !== "string" || !value.startsWith("/")) return "";
  if (value.startsWith("//") || value.includes("\\") || value.includes("\n") || value.includes("\r")) return "";
  return value.slice(0, 240);
}

function boundedText(value: unknown, fallback: string): string {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, 96) : fallback;
}

function loginPathFor(customerId: string, providerId: string, loginPath?: string, returnTo?: string): string {
  const path = safeSameOriginPath(loginPath || "/auth/login");
  if (!path) return "";
  const query = new URLSearchParams();
  // ALL-5718: only a same-origin path is forwarded; the gateway checks it again.
  query.set("return_to", safeSameOriginPath(returnTo) || "/");
  if (customerId) query.set("customer", customerId);
  if (providerId) query.set("provider", providerId);
  return path + "?" + query.toString();
}

const DEFAULT_AUTH_METHODS: AuthMethod[] = [
  {
    id: "google-oidc",
    label: "Continue with Google",
    detail: "Google Workspace OIDC single sign-on",
    startUrl: "/auth/login?return_to=/",
    enabled: true,
    primary: true,
  },
];

function parseAuthMethods(config: Record<string, unknown>, returnTo?: string): AuthMethod[] {
  const customers = Array.isArray(config.customers) ? config.customers : [];
  const methods: AuthMethod[] = [];

  for (const customer of customers) {
    if (!customer || typeof customer !== "object") continue;
    const customerId = typeof (customer as Record<string, unknown>).customerId === "string"
      ? ((customer as Record<string, unknown>).customerId as string).trim()
      : "";
    const providers = Array.isArray((customer as Record<string, unknown>).providers)
      ? (customer as Record<string, unknown>).providers as Record<string, unknown>[]
      : [];

    for (const provider of providers) {
      if (!provider || typeof provider !== "object") continue;
      const providerId = typeof provider.providerId === "string" ? provider.providerId.trim() : "";
      const type = boundedText(provider.type, "oidc").toLowerCase();
      const browserStartSupported = provider.browserStartSupported === true;
      const enabled = provider.enabled === true && provider.browserLoginEnabled === true && browserStartSupported;
      const displayName = boundedText(provider.displayName, "");
      const isGoogle = /google/i.test(displayName) || providerId === "google";

      methods.push({
        id: providerId === "google" ? "google-oidc" : `provider-${providerId || methods.length + 1}`,
        label: isGoogle ? "Continue with Google" : (displayName || (type === "saml" ? "Enterprise SAML" : "Enterprise SSO")),
        detail: browserStartSupported && type === "oidc"
          ? "Gateway-managed OIDC single sign-on"
          : type === "saml"
            ? "Configured for this tenant; browser start is not enabled"
            : "Configured for this tenant; not enabled for browser login",
        startUrl: enabled ? loginPathFor(customerId, providerId, provider.loginPath as string | undefined, returnTo) : "",
        enabled,
        primary: isGoogle || methods.length === 0,
      });
    }
  }

  if (Array.isArray(config.authMethods)) {
    for (const method of config.authMethods as Record<string, unknown>[]) {
      if (!method || typeof method !== "object" || typeof method.id !== "string") continue;
      methods.push({
        id: method.id,
        label: boundedText(method.label, "Sign in"),
        detail: boundedText(method.detail, ""),
        startUrl: safeSameOriginPath(method.startUrl),
        enabled: method.enabled === true,
        primary: method.primary === true,
      });
    }
  }

  const best = new Map<string, AuthMethod>();
  for (const m of methods) {
    const existing = best.get(m.id);
    if (!existing) { best.set(m.id, m); continue; }
    const score = (x: AuthMethod) => (x.enabled ? 2 : 0) + (x.primary ? 1 : 0);
    if (score(m) > score(existing)) best.set(m.id, m);
  }
  return Array.from(best.values());
}

export async function fetchAuthOptions(returnTo?: string): Promise<AuthMethod[]> {
  const served = await fetchServedAuthOptions(returnTo);
  if (served) return served;
  // The compiled-in fallback carries the same validated return path (ALL-5718).
  const safeReturn = safeSameOriginPath(returnTo) || "/";
  if (safeReturn === "/") return DEFAULT_AUTH_METHODS;
  return DEFAULT_AUTH_METHODS.map((method) => ({
    ...method,
    startUrl: method.startUrl === "/auth/login?return_to=/"
      ? `/auth/login?return_to=${encodeURIComponent(safeReturn)}`
      : method.startUrl,
  }));
}

/**
 * The sign-in methods exactly as GET /auth/options served them, or null when the
 * call failed or served none. Unlike fetchAuthOptions there is no compiled-in
 * fallback: a step-up re-authentication must go through a provider this
 * deployment actually serves, so an unavailable /auth/options means "cannot
 * start", never "guess a provider".
 */
export async function fetchServedAuthOptions(returnTo?: string): Promise<AuthMethod[] | null> {
  try {
    const res = await apiFetch("/auth/options", {}, "auth_config");
    if (!res.ok) return null;
    const config = await res.json();
    if (!config || typeof config !== "object") return null;
    const methods = parseAuthMethods(config as Record<string, unknown>, returnTo);
    return methods.length > 0 ? methods : null;
  } catch {
    return null;
  }
}

export async function logout(): Promise<void> {
  // A write parked for a step-up resume belongs to this sign-in only: drop it
  // before the session ends so no later sign-in in this tab can find it.
  clearPendingStepUpResume();
  await apiFetch("/auth/logout", { method: "POST" }, "logout");
}

// --- Terms acceptance (ALL-5722, specs/terms-acceptance-gate S-6..S-8) ---

export interface CurrentTerms {
  version: string;
  title: string;
  effectiveAt: string;
  documentMarkdown: string;
  documentSha256: string;
  acceptedByCaller: boolean;
}

export type CurrentTermsResult =
  | { ok: true; terms: CurrentTerms }
  | { ok: false; supportReference?: string };

function isCurrentTerms(value: unknown): value is CurrentTerms {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.version === "string" &&
    typeof v.title === "string" &&
    typeof v.effectiveAt === "string" &&
    typeof v.documentMarkdown === "string" &&
    typeof v.documentSha256 === "string" &&
    typeof v.acceptedByCaller === "boolean"
  );
}

/** GET /v1/terms/current. Never throws; a failure carries the server's support reference. */
export async function fetchCurrentTerms(): Promise<CurrentTermsResult> {
  let res: Response;
  try {
    res = await apiFetch("/v1/terms/current", {}, "terms_current");
  } catch {
    return { ok: false };
  }
  const body = await res.json().catch(() => null);
  if (!res.ok || !isCurrentTerms(body)) return { ok: false, ...responseSupport(res, body) };
  return { ok: true, terms: body };
}

export type AcceptTermsResult =
  | { ok: true; acceptanceId: string; version: string; acceptedAt: string }
  | { ok: false; versionChanged: boolean; supportReference?: string };

/**
 * POST /v1/terms/acceptances with the version and hash of the document the
 * person saw. 409 TERMS_VERSION_NOT_CURRENT means the terms changed while they
 * were reading. The CSRF header is added by apiFetchWithCorrelation.
 */
export async function acceptTerms(version: string, documentSha256: string): Promise<AcceptTermsResult> {
  let res: Response;
  try {
    res = await apiFetch("/v1/terms/acceptances", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Idempotency-Key": idempotencyKey("terms_accept") },
      body: JSON.stringify({ version, documentSha256 }),
    }, "terms_accept");
  } catch {
    return { ok: false, versionChanged: false };
  }
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  const reason = body ? body.reasonCode ?? body.reason_code : undefined;
  if (res.status === 409 && reason === "TERMS_VERSION_NOT_CURRENT") return { ok: false, versionChanged: true, ...responseSupport(res, body) };
  if (!res.ok || !body || typeof body.acceptanceId !== "string") {
    return { ok: false, versionChanged: false, ...responseSupport(res, body) };
  }
  return {
    ok: true,
    acceptanceId: body.acceptanceId,
    version: typeof body.version === "string" ? body.version : version,
    acceptedAt: typeof body.acceptedAt === "string" ? body.acceptedAt : "",
  };
}

// --- Shared sandbox org/environment context ---

export interface ExperienceContractResult {
  contract: ExperienceContract;
  /**
   * True when `/v1/session/effective-access` failed (network error or a
   * non-2xx status) — same meaning and field name as `SessionInfo.transportError`
   * above, kept consistent across both session-bootstrap calls so a caller
   * never has to remember two different flag names for "the network call
   * itself failed". `contract` is still a well-typed `EXPERIENCE_CONTRACT_UNKNOWN`
   * in that case so callers never have to null-check, but it must never be
   * rendered as the live shell — callers branch on this flag and show an
   * explicit error state instead (see `stores/auth-store.ts`).
   */
  transportError?: boolean;
}

export async function fetchExperienceContract(): Promise<ExperienceContractResult> {
  let res: Response;
  try {
    res = await apiFetch("/v1/session/effective-access", {}, "effective_access");
  } catch {
    return { contract: EXPERIENCE_CONTRACT_UNKNOWN, transportError: true };
  }
  if (!res.ok) return { contract: EXPERIENCE_CONTRACT_UNKNOWN, transportError: true };
  try {
    const data = await res.json();
    return { contract: normalizeExperienceContract(data, "api") };
  } catch {
    return { contract: EXPERIENCE_CONTRACT_UNKNOWN, transportError: true };
  }
}

/**
 * Switches the active organization. Returns `null` on any failure (network
 * error or non-2xx) rather than a fixture-backed contract, so a failed
 * switch never silently replaces a real organization with fictional demo
 * data — the caller keeps the last-known-good `experience` untouched.
 */
export async function setActiveOrganization(organizationId: string): Promise<ExperienceContract | null> {
  let res: Response;
  try {
    res = await apiFetch("/v1/session/active-organization", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ organizationId }),
    }, "active_org");
  } catch {
    return null;
  }
  if (!res.ok) return null;
  try {
    const data = await res.json();
    return normalizeExperienceContract(data, "api");
  } catch {
    return null;
  }
}

// --- Wallets ---

export interface ApiErrorInfo {
  reasonCode: string;
  /** Server-issued support reference (D636): the X-Alloy-Support-Reference
   * header, else the body's supportReference. Never the client's own
   * correlation id. */
  supportReference?: string;
  /** Missing capability named by a 403 body, display-only. */
  capability?: string;
}

/** Support reference and 403 capability for a response, in the optional-field
 * shape the API result types use. See i18n/support-reference.ts. */
export interface ResponseSupport {
  supportReference?: string;
  capability?: string;
}

export function responseSupport(res: Response, data?: unknown): ResponseSupport {
  const info = extractSupportInfo({ response: res, body: data });
  return {
    supportReference: info.supportReference ?? undefined,
    capability: info.capability ?? undefined,
  };
}

export interface WalletListResult {
  wallets: Record<string, unknown>[];
  /**
   * ALL-6339: the gateway's opaque cursor for the next page of wallets, or null on the last page.
   * WalletKit answers at most 100 wallets per call, so a caller with more follows this token.
   */
  nextPageToken?: string | null;
  error?: ApiErrorInfo;
}

export function boundedErrorText(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, 160) : undefined;
}

export function responseReasonCode(data: Record<string, unknown>): string | undefined {
  return boundedErrorText(data.reason_code)
    || boundedErrorText(data.reasonCode);
}

function extractApiError(res: Response, data: Record<string, unknown>): ApiErrorInfo {
  return {
    reasonCode: responseReasonCode(data)
      || boundedErrorText(data.error)
      || boundedErrorText(data.message)
      || `HTTP_${res.status}`,
    ...responseSupport(res, data),
  };
}

export interface ApiListResponse<T = Record<string, unknown>> {
  items: T[];
  total?: number;
  nextPageToken?: string | null;
  hasMore?: boolean;
  error?: ApiErrorInfo;
}

export async function fetchList<T = Record<string, unknown>>(
  path: string,
  listKey: string,
  scope: string,
  signal?: AbortSignal,
): Promise<ApiListResponse<T>> {
  const { response: res } = await apiFetchWithCorrelation(path, { signal }, scope);
  const data = await res.json();
  if (!res.ok) {
    const body = data && typeof data === "object" ? data as Record<string, unknown> : {};
    return { items: [], error: extractApiError(res, body) };
  }
  const items = Array.isArray(data[listKey]) ? data[listKey]
    : Array.isArray(data) ? data
    : [];
  return {
    items: items as T[],
    total: typeof data.total === "number" ? data.total : undefined,
    nextPageToken: typeof data.nextPageToken === "string" || data.nextPageToken === null
      ? data.nextPageToken
      : undefined,
    hasMore: typeof data.hasMore === "boolean" ? data.hasMore : undefined,
  };
}

export interface ApiWebhookEndpoint {
  id: string;
  url: string;
  eventTypes: string[];
  active: boolean;
  createdAt: string;
}

export function fetchWebhooks(signal?: AbortSignal): Promise<ApiListResponse<ApiWebhookEndpoint>> {
  return fetchList<ApiWebhookEndpoint>(
    "/v1/webhooks?page_size=5",
    "items",
    "webhook_list",
    signal,
  );
}

export interface ApiDetailResponse<T = Record<string, unknown>> {
  item: T | null;
  error?: ApiErrorInfo;
}

export async function fetchDetail<T = Record<string, unknown>>(
  path: string,
  scope: string,
): Promise<ApiDetailResponse<T>> {
  const { response: res } = await apiFetchWithCorrelation(path, {}, scope);
  const data = await res.json();
  if (!res.ok) {
    const body = data && typeof data === "object" ? data as Record<string, unknown> : {};
    return { item: null, error: extractApiError(res, body) };
  }
  return { item: data as T };
}

export interface ApiMutationResult extends ResponseSupport {
  ok: boolean;
  error?: string;
  data?: Record<string, unknown>;
}

export async function apiMutate(
  path: string,
  method: string,
  body: unknown,
  scope: string,
): Promise<ApiMutationResult> {
  try {
    const res = await apiFetch(path, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }, scope);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        ok: false,
        error: (data as Record<string, string>)?.message
          || (data as Record<string, string>)?.reason_code
          || (data as Record<string, string>)?.reasonCode
          || `HTTP ${res.status}`,
        ...responseSupport(res, data),
      };
    }
    return { ok: true, data: data as Record<string, unknown>, ...responseSupport(res, data) };
  } catch {
    return { ok: false, error: "Network error" };
  }
}

/**
 * GET /v1/walletkit/wallets: one page of the caller's wallets. Without a token this is the first
 * page (the same 100 rows the route always served); pass the previous page's `nextPageToken` to read
 * the next one. The token is opaque and only ever echoed back to the gateway.
 */
export async function fetchWallets(pageToken?: string | null): Promise<WalletListResult> {
  const path = pageToken
    ? `/v1/walletkit/wallets?page_token=${encodeURIComponent(pageToken)}`
    : "/v1/walletkit/wallets";
  const { response: res } = await apiFetchWithCorrelation(
    path,
    {},
    "wallet_list",
  );
  const data = await res.json();
  if (!res.ok) {
    const body = data && typeof data === "object" ? data as Record<string, unknown> : {};
    return {
      wallets: [],
      error: {
        reasonCode: responseReasonCode(body) || boundedErrorText(body.error) || `HTTP_${res.status}`,
        ...responseSupport(res, body),
      },
    };
  }
  const next = typeof data.nextPageToken === "string" && data.nextPageToken.length > 0 ? data.nextPageToken : null;
  return { wallets: data.wallets || [], nextPageToken: next };
}

/**
 * A wallet id the console will look up directly: letters, digits, "_" and "-" only, starting with a
 * letter or digit, 3 to 128 characters. That covers the ids WalletKit issues ("wlt_" plus hex) and
 * rules out dots, colons, slashes, spaces and anything else that is not part of an id, so a pasted
 * paragraph or a path-like value is never sent to the gateway.
 */
export const WALLET_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{2,127}$/;

export interface WalletLookupResult {
  /** The wallet row (the same shape as a `GET /v1/walletkit/wallets` row), or null. */
  wallet: Record<string, unknown> | null;
  /** True when the gateway answered 404: the wallet does not exist or is not visible to this caller. */
  notFound?: boolean;
  error?: ApiErrorInfo;
  /** The correlation id of the request, so a refused or failed lookup can be joined to the gateway's record. */
  correlationId?: string;
}

/**
 * ALL-6339: read one wallet by id with `GET /v1/walletkit/wallets/{id}`, the route the wallet list
 * itself is served from (same scope, same trust-scoped alias prefix). The transfer picker uses it
 * to reach a wallet that is not on the loaded pages: a deep link (`/transfers/new?source=<id>`) or
 * a wallet id typed into the picker's search. A wallet the caller cannot see is a 404 here, never
 * someone else's row.
 */
export async function fetchWalletById(id: string): Promise<WalletLookupResult> {
  const walletId = id.trim();
  if (!WALLET_ID_PATTERN.test(walletId)) return { wallet: null, notFound: true };
  const { response: res, correlationId } = await apiFetchWithCorrelation(
    `/v1/walletkit/wallets/${encodeURIComponent(walletId)}`,
    {},
    "wallet_lookup",
  );
  const data = await res.json().catch(() => null);
  const body = data && typeof data === "object" ? data as Record<string, unknown> : {};
  if (!res.ok) {
    if (res.status === 404) return { wallet: null, notFound: true, correlationId };
    return {
      wallet: null,
      correlationId,
      error: {
        reasonCode: responseReasonCode(body) || boundedErrorText(body.error) || `HTTP_${res.status}`,
        ...responseSupport(res, body),
      },
    };
  }
  const row = body.wallet && typeof body.wallet === "object" && !Array.isArray(body.wallet)
    ? body.wallet as Record<string, unknown>
    : null;
  // The answer must be the wallet that was asked for; anything else is treated as not found.
  if (!row || String(row.walletId ?? row.id ?? "") !== walletId) return { wallet: null, notFound: true, correlationId };
  return { wallet: row, correlationId };
}

export interface WalletOptionsResult {
  networks: { chain: string; network: string; enabled: boolean; disabledReasonCode?: string | null }[];
  /**
   * The admin-configured asset mapping for each chain/network. Read-only here:
   * a wallet is created for a chain family, not for a single asset (PM review
   * `create-wallet-pm-review.md`, decision 1), so the create flow only
   * *previews* what the wallet will be able to receive.
   */
  assets: {
    symbol: string;
    name: string;
    decimals: number;
    chain: string;
    network: string;
    enabled: boolean;
    disabledReasonCode?: string | null;
  }[];
  providerProfiles: { walletProfileId: string; displayName: string; enabled: boolean; disabledReasonCode?: string | null }[];
  /** ALL-4944: the admin-configured default route; null/absent on gateways before ALL-4943. */
  defaultWalletProfileId?: string | null;
}

export type WalletOptionsResponse =
  | { ok: true; options: WalletOptionsResult }
  | ({ ok: false; reasonCode: string } & ResponseSupport);

/**
 * GET /v1/wallets/new — the admin-configured networks and provider routes the
 * caller may create a wallet on. Failures are returned (never swallowed), so the
 * create flow can show a bounded error instead of falling back to fixture data.
 */
export async function fetchWalletOptions(): Promise<WalletOptionsResponse> {
  let res: Response;
  try {
    res = await apiFetch("/v1/wallets/new", {}, "wallet_options");
  } catch {
    return { ok: false, reasonCode: "NETWORK_ERROR" };
  }
  const data = await res.json().catch(() => null);
  if (!res.ok || !data || typeof data !== "object") {
    const body = (data && typeof data === "object" ? data : {}) as Record<string, unknown>;
    return {
      ok: false,
      reasonCode: responseReasonCode(body) || `HTTP_${res.status}`,
      ...responseSupport(res, data),
    };
  }
  const raw = data as Partial<WalletOptionsResult>;
  return {
    ok: true,
    options: {
      networks: Array.isArray(raw.networks) ? raw.networks : [],
      assets: Array.isArray(raw.assets) ? raw.assets : [],
      providerProfiles: Array.isArray(raw.providerProfiles) ? raw.providerProfiles : [],
      defaultWalletProfileId: typeof raw.defaultWalletProfileId === "string" ? raw.defaultWalletProfileId : null,
    },
  };
}

export interface CreateWalletResult extends ResponseSupport {
  ok: boolean;
  walletId?: string;
  error?: string;
  reasonCode?: string;
  status?: number;
}

/**
 * POST /v1/walletkit/wallets. The gateway's idempotency envelope requires an
 * `Idempotency-Key`; without it every create is refused with 400 before any
 * business check (ALL-4839). Callers pass the same key only when retrying the
 * same request, so a retry after a lost response replays instead of duplicating.
 */
export async function createWallet(
  accountAlias: string,
  opts?: { chain?: string; network?: string; walletProfileId?: string; idempotencyKey?: string },
): Promise<CreateWalletResult> {
  const body: Record<string, string> = { accountAlias };
  if (opts?.chain) body.chain = opts.chain;
  if (opts?.network) body.network = opts.network;
  if (opts?.walletProfileId) body.walletProfileId = opts.walletProfileId;

  let res: Response;
  try {
    res = await apiFetch("/v1/walletkit/wallets", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Idempotency-Key": opts?.idempotencyKey || idempotencyKey("wallet_create"),
      },
      body: JSON.stringify(body),
    }, "wallet_create");
  } catch {
    return { ok: false, error: "Network error", reasonCode: "NETWORK_ERROR" };
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const record = (data && typeof data === "object" ? data : {}) as Record<string, unknown>;
    const reasonCode = responseReasonCode(record) || `HTTP_${res.status}`;
    return {
      ok: false,
      status: res.status,
      reasonCode,
      error: boundedErrorText(record.detail) || boundedErrorText(record.message) || reasonCode,
      ...responseSupport(res, data),
    };
  }
  // D636: the create's own reference rides on the success confirmation.
  return { ok: true, walletId: data?.walletId, ...responseSupport(res, data) };
}

// --- Bootstrap ---

export async function previewBootstrapInvite(rawInviteToken: string): Promise<Record<string, unknown> | null> {
  try {
    const res = await apiFetch("/auth/bootstrap/bootstrap-invites/preview", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rawInviteToken }),
    }, "bootstrap_preview");
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

export async function acceptBootstrapInvite(rawInviteToken: string): Promise<{ ok: boolean; organizationId?: string; error?: string }> {
  try {
    const res = await apiFetch("/auth/bootstrap/bootstrap-invites/accept", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rawInviteToken }),
    }, "bootstrap_accept");
    const data = await res.json();
    if (!res.ok) {
      return { ok: false, error: data?.message || "Activation failed" };
    }
    return { ok: true, organizationId: data.organizationId };
  } catch {
    return { ok: false, error: "Activation failed" };
  }
}

// --- Admin invite acceptance (ALL-4851) ---
//
// GAP (recorded in ALL-4851 and the PR body for claude/invite-accept-page):
// as of context#994 / alloy-auth-service main, AdminBootstrapLifecycleService
// exposes `acceptAdminInvite(InviteAcceptanceRequest)` — a raw-token plus a
// passkey/WebAuthn credential-binding contract (fields this browser app must
// never construct or reference directly) — via
// `POST /public/auth/admin-bootstrap/admin-invites/accept`, but:
//   1. There is no browser-session (Google OAuth) variant for admin invites.
//      `acceptBootstrapInviteFromBrowserSession` exists for *bootstrap*
//      invites only; no `acceptAdminInviteFromBrowserSession` equivalent
//      exists in AdminBootstrapLifecycleService/AdminBootstrapController.
//   2. There is no admin-invite preview endpoint. `previewBootstrapInvite`
//      (above) exists for bootstrap invites only; `PrincipalResult` (the
//      admin-invite accept response) carries only
//      `principalId/organizationId/principalType/status` — no organization
//      name, inviter name/email, or invitee email.
//   3. There is no gateway route in this app's same-origin `/auth/*` surface
//      proxying either admin-invite endpoint today (compare
//      `/auth/bootstrap/bootstrap-invites/{preview,accept}` above, which do
//      exist and are proxied).
//
// The functions below call the same-origin paths this page needs
// (`/auth/admin-invites/preview`, `/auth/admin-invites/accept`), mirroring
// the bootstrap-invite gateway proxy shape 1:1 so wiring is a route-add, not
// a frontend rewrite, once the backend contract above is closed. Until then
// every call 404s in every real environment; the page (src/pages/auth/
// invite-accept.tsx) treats any non-2xx/network failure on the preview call
// as "no organization/inviter preview available" (never fatal — sign-in
// still proceeds) and on the accept call as a generic, retryable error
// state — never a fabricated success. See ALL-4851.
export interface AdminInvitePreviewResult {
  email?: string;
  /** The role the invite grants (auth AdminInvitePreviewResult.roleName), e.g. transaction_signer. */
  roleName?: string;
  organizationName?: string;
  inviterName?: string;
  inviterEmail?: string;
  status?: string;
  reasonCode?: string;
}

export async function previewAdminInvite(rawInviteToken: string): Promise<AdminInvitePreviewResult | null> {
  try {
    const res = await apiFetch("/auth/admin-invites/preview", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rawInviteToken }),
    }, "admin_invite_preview");
    if (!res.ok) return null;
    return (await res.json()) as AdminInvitePreviewResult;
  } catch {
    return null;
  }
}

export interface AdminInviteAcceptResult {
  ok: boolean;
  principalId?: string;
  organizationId?: string;
  reasonCode?: string;
  error?: string;
}

export async function acceptAdminInvite(rawInviteToken: string): Promise<AdminInviteAcceptResult> {
  try {
    const res = await apiFetch("/auth/admin-invites/accept", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rawInviteToken }),
    }, "admin_invite_accept");
    let data: Record<string, unknown> = {};
    try {
      data = await res.json();
    } catch {
      data = {};
    }
    if (!res.ok) {
      return {
        ok: false,
        reasonCode: (data?.reasonCode as string) || (data?.reason_code as string) || undefined,
        error: (data?.message as string) || "Invite acceptance failed",
      };
    }
    return { ok: true, principalId: data.principalId as string, organizationId: data.organizationId as string };
  } catch {
    return { ok: false, error: "Invite acceptance failed" };
  }
}

// --- Transfers ---

export async function fetchTransfers(walletId?: string): Promise<{ transfers: Record<string, unknown>[]; reasonCode?: string }> {
  const path = walletId
    ? `/v1/transfers?wallet_id=${encodeURIComponent(walletId)}&page_size=20`
    : "/v1/transfers";
  const res = await apiFetch(path, {}, "transfer_list");
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    // The shared transfer-list caller retains the gateway's structured reason.
    // Wallet detail needs HTTP denial status when a freeform message is all the
    // gateway supplies, so its Recent Transfers panel can explain permissions.
    if (!walletId) {
      return { transfers: [], reasonCode: data?.reason_code || data?.reasonCode || data?.message || `HTTP ${res.status}` };
    }
    // HTTP status is authoritative for denial and service failures. A freeform
    // gateway message such as "Forbidden" must not turn a 403 into a generic error.
    const gatewayReason = data?.reason_code ?? data?.reasonCode;
    const authReason = typeof gatewayReason === "string" && /^AUTH_[A-Z0-9_]{1,59}$/.test(gatewayReason);
    const reasonCode = res.status === 401 || res.status === 403
      ? (authReason ? gatewayReason : `HTTP ${res.status}`)
      : res.status >= 500 ? `HTTP ${res.status}` : gatewayReason || `HTTP ${res.status}`;
    return { transfers: [], reasonCode };
  }
  return { transfers: Array.isArray(data.transfers) ? data.transfers : Array.isArray(data) ? data : [] };
}

/**
 * One page of `GET /v1/transfers` (ALL-5793). The gateway pages newest first with an opaque
 * `next_page_token`; `wallet_id` narrows the read to one source wallet on the server. Asset,
 * network, date and free-text filters do not exist on the route yet (ALL-5801), so the console
 * applies those to the pages it has loaded and says so.
 */
export interface TransferPage {
  transfers: Record<string, unknown>[];
  nextPageToken: string | null;
  reasonCode?: string;
}

/**
 * Server-side filters for `GET /v1/transfers` (ALL-5801). The gateway applies
 * these to the whole result set before pagination, so a filtered page is a page
 * of the filtered list, not a page filtered after the fact. `created_after` /
 * `created_before` are half-open RFC 3339 instants, `asset` and `network`
 * accept comma lists, and `q` must be 3–128 characters.
 *
 * The next page token is bound to the filter set it was issued for: reuse it
 * with different filters and the gateway answers `400 PAGE_TOKEN_MISMATCH`, so
 * a filter change must restart paging with no token.
 */
export interface TransferQuery {
  walletId?: string;
  asset?: string;
  network?: string;
  status?: string;
  /** Free-text search, 3–128 characters; shorter values are not sent. */
  q?: string;
  createdAfter?: string;
  createdBefore?: string;
  pageToken?: string | null;
  pageSize?: number;
}

export async function fetchTransferPage(params: TransferQuery = {}): Promise<TransferPage> {
  const query = new URLSearchParams();
  query.set("page_size", String(params.pageSize ?? 50));
  if (params.walletId) query.set("wallet_id", params.walletId);
  if (params.asset) query.set("asset", params.asset);
  if (params.network) query.set("network", params.network);
  if (params.status) query.set("status", params.status);
  if (params.q) query.set("q", params.q);
  if (params.createdAfter) query.set("created_after", params.createdAfter);
  if (params.createdBefore) query.set("created_before", params.createdBefore);
  if (params.pageToken) query.set("page_token", params.pageToken);
  const res = await apiFetch(`/v1/transfers?${query.toString()}`, {}, "transfer_list");
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const gatewayReason = data?.reason_code ?? data?.reasonCode;
    const authReason = typeof gatewayReason === "string" && /^AUTH_[A-Z0-9_]{1,59}$/.test(gatewayReason);
    // HTTP status is authoritative for denial and service failures, as in fetchTransfers.
    const reasonCode = res.status === 401 || res.status === 403
      ? (authReason ? gatewayReason : `HTTP ${res.status}`)
      : (typeof gatewayReason === "string" && gatewayReason) || (typeof data?.message === "string" && data.message) || `HTTP ${res.status}`;
    return { transfers: [], nextPageToken: null, reasonCode };
  }
  const transfers = Array.isArray(data?.transfers) ? data.transfers : Array.isArray(data) ? data : [];
  const token = typeof data?.nextPageToken === "string" && data.nextPageToken ? data.nextPageToken
    : typeof data?.next_page_token === "string" && data.next_page_token ? data.next_page_token : null;
  return { transfers, nextPageToken: token };
}

export async function fetchTransferDetail(id: string): Promise<{ transfer: Record<string, unknown> | null; reasonCode?: string }> {
  const res = await apiFetch(`/v1/transfers/${encodeURIComponent(id)}`, {}, "transfer_detail");
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return { transfer: null, reasonCode: data?.reason_code || data?.reasonCode || data?.message || `HTTP ${res.status}` };
  }
  return { transfer: (data && typeof data === "object" && "transfer" in data) ? (data as Record<string, unknown>).transfer as Record<string, unknown> : null };
}

export interface TransferDestinationInput {
  type: "internal" | "external";
  walletId?: string;
  address?: string;
  network: string;
}

/**
 * `POST /v1/transfers/estimate` (`TransferEstimateRequest`,
 * alloy-api-gateway-service) takes a nested `destination` object
 * (`{type, walletId, address, network}`), not a flat `destinationAddress`
 * field — sending the flat shape used by the drafts endpoint gets rejected
 * before the auth/policy layer even runs. See
 * contract-snapshots/transfers-new/ for the live evidence of both shapes.
 */
export async function fetchTransferEstimate(params: {
  sourceWalletId: string;
  destination: TransferDestinationInput;
  asset: string;
  amount: string;
}): Promise<{ estimate: Record<string, unknown> | null; error?: string; reasonCode?: string }> {
  try {
    const res = await apiFetch("/v1/transfers/estimate", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Idempotency-Key": idempotencyKey("transfer_estimate") },
      body: JSON.stringify(params),
    }, "transfer_estimate");
    const data = await res.json();
    if (!res.ok) {
      return {
        estimate: null,
        error: data?.detail || data?.message || "Estimate failed",
        reasonCode: data?.reason_code || data?.reasonCode,
      };
    }
    return { estimate: data };
  } catch {
    return { estimate: null, error: "Estimate unavailable" };
  }
}

export async function createTransferDraft(draft: Record<string, unknown>): Promise<{ ok: boolean; transferId?: string; error?: string } & ResponseSupport> {
  const res = await apiFetch("/v1/transfers/drafts", {
    method: "POST",
    headers: { "Content-Type": "application/json", "Idempotency-Key": idempotencyKey("transfer_draft_create") },
    body: JSON.stringify(draft),
  }, "transfer_draft_create");
  const data = await res.json();
  if (!res.ok) {
    return { ok: false, error: data?.detail || data?.message || data?.reason_code || data?.reasonCode || "Draft creation failed", ...responseSupport(res, data) };
  }
  return { ok: true, transferId: data.transferId || data.id, ...responseSupport(res, data) };
}

export async function submitTransferDraft(draftId: string): Promise<{ ok: boolean; error?: string; reasonCode?: string; intentId?: string } & ResponseSupport> {
  // Bind the retry to this draft and the original submission envelope.
  const key = idempotencyKey("transfer_draft_submit");
  const outcome = await withStepUpRetry(async () => {
    const res = await apiFetch(`/v1/transfers/drafts/${encodeURIComponent(draftId)}/submit`, {
      method: "POST",
      headers: { "Idempotency-Key": key },
    }, "transfer_draft_submit");
    const body = await res.json().catch(() => ({}));
    const data = body && typeof body === "object" && !Array.isArray(body) ? body : {};
    if (!res.ok) {
      return { ok: false, reasonCode: responseReasonCode(data), error: (data as Record<string, string>)?.detail || (data as Record<string, string>)?.message || "Submit failed", ...responseSupport(res, data) };
    }
    // ALL-5794: the submitted draft names the governed intent it became (TransferDraftResponse.intentId).
    // GET /v1/transfers/{id} reads intents, not drafts, so the detail page needs this id.
    const intentId = typeof (data as Record<string, unknown>)?.intentId === "string" && (data as Record<string, string>).intentId.trim()
      ? (data as Record<string, string>).intentId.trim()
      : undefined;
    return { ok: true, intentId, ...responseSupport(res, data) };
  });
  return outcome.result;
}

// --- Approvals ---

// status maps to the gateway's real server-side queue scope
// (ApprovalController.normalizeScope): "mine" | "all-pending" | "completed" | "escalated".
export type ApprovalQueueScope = "mine" | "all-pending" | "completed" | "escalated";

export async function fetchApprovals(status?: ApprovalQueueScope): Promise<{ approvals: Record<string, unknown>[]; reasonCode?: string }> {
  const query = status ? `?status=${encodeURIComponent(status)}` : "";
  const res = await apiFetch(`/v1/approvals${query}`, {}, "approval_list");
  const data = await res.json();
  if (!res.ok) {
    return { approvals: [], reasonCode: data?.reason_code || data?.reasonCode || data?.message || `HTTP ${res.status}` };
  }
  return { approvals: Array.isArray(data.approvals) ? data.approvals : Array.isArray(data) ? data : [] };
}

export async function fetchApprovalDetail(id: string): Promise<{ approval: Record<string, unknown> | null; reasonCode?: string }> {
  const res = await apiFetch(`/v1/approvals/${encodeURIComponent(id)}`, {}, "approval_detail");
  const data = await res.json();
  if (!res.ok) {
    return { approval: null, reasonCode: data?.reason_code || data?.reasonCode || data?.message || `HTTP ${res.status}` };
  }
  return { approval: data };
}

function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// The gateway's GatewayIdempotencyEnvelopeFilter requires an Idempotency-Key
// header on every mutating request, and ApprovalController's
// GatewayApprovalActionRequest body only accepts { comment, idempotencyKey }
// (not { reason }). Both approve and reject failed against the real backend
// before this fix (ALL-4734 G2/G5 — confirmed live via scripts/dev-api.sh).
export async function approveApproval(
  id: string,
  comment?: string,
  idempotencyKey?: string,
): Promise<{ ok: boolean; error?: string; reasonCode?: string } & ResponseSupport> {
  // A caller-supplied idempotencyKey (ALL-5022) lets withStepUpRetry replay the exact same
  // decision after a step-up round trip without the gateway seeing it as two different
  // requests; omit it (the common case) and a fresh key is minted as before.
  idempotencyKey = idempotencyKey || newIdempotencyKey();
  const res = await apiFetch(`/v1/approvals/${encodeURIComponent(id)}/approve`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "Idempotency-Key": idempotencyKey },
    body: JSON.stringify({ comment, idempotencyKey }),
  }, "approval_approve");
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = data as Record<string, unknown>;
    return {
      ok: false,
      error: String(err?.message ?? "Approve failed"),
      reasonCode: err?.reasonCode ? String(err.reasonCode) : err?.reason_code ? String(err.reason_code) : undefined,
      ...responseSupport(res, data),
    };
  }
  return { ok: true, ...responseSupport(res, data) };
}

export async function rejectApproval(
  id: string,
  comment: string,
  idempotencyKey?: string,
): Promise<{ ok: boolean; error?: string; reasonCode?: string } & ResponseSupport> {
  idempotencyKey = idempotencyKey || newIdempotencyKey();
  const res = await apiFetch(`/v1/approvals/${encodeURIComponent(id)}/reject`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "Idempotency-Key": idempotencyKey },
    body: JSON.stringify({ comment, idempotencyKey }),
  }, "approval_reject");
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = data as Record<string, unknown>;
    return {
      ok: false,
      error: String(err?.message ?? "Reject failed"),
      reasonCode: err?.reasonCode ? String(err.reasonCode) : err?.reason_code ? String(err.reason_code) : undefined,
      ...responseSupport(res, data),
    };
  }
  return { ok: true, ...responseSupport(res, data) };
}

// --- Wallet detail ---

export interface WalletDetailResult {
  wallet: Record<string, unknown> | null;
  reasonCode?: string;
  /**
   * "detail" when `GET /v1/wallets/{id}` answered (its `balances` / `balanceStatus` are
   * authoritative); "list" when it failed and the wallet came from the list fallback, which never
   * carries balances — callers must then treat balances as unknown, not zero (ALL-4839).
   */
  source?: "detail" | "list";
  /** Support reference of the failed detail call, when the list fallback was used. */
  supportReference?: string;
  /** HTTP status of the failed detail call, when there was a response. */
  status?: number;
  /** Missing capability named by a 403 body, display-only. */
  capability?: string;
  /** True only when the wallet is known to be absent (404, or the list answered and omits it). */
  notFound?: boolean;
}

export async function fetchWalletDetail(id: string): Promise<WalletDetailResult> {
  let detailSupportReference: string | undefined;
  let capability: string | undefined;
  let status: number | undefined;
  let reasonCode: string | undefined;
  try {
    const res = await apiFetch(`/v1/wallets/${encodeURIComponent(id)}`, {}, "wallet_detail");
    const data = await res.json();
    if (res.ok) return { wallet: data, source: "detail" };
    status = res.status;
    const support = responseSupport(res, data);
    detailSupportReference = support.supportReference;
    capability = support.capability;
    reasonCode = data && typeof data === "object" ? responseReasonCode(data as Record<string, unknown>) : undefined;
  } catch { /* fall through to list fallback */ }

  let listAnswered = false;
  try {
    const listResult = await fetchWallets();
    const match = listResult.wallets.find(
      (w: Record<string, unknown>) => w.walletId === id || w.id === id,
    );
    if (match) return { wallet: match, source: "list", supportReference: detailSupportReference };
    listAnswered = !listResult.error;
  } catch { /* ignore */ }

  // The list answered and omits the wallet: it is not visible to this caller, so it is "not found".
  const notFound = status === 404 || listAnswered;
  return {
    wallet: null,
    reasonCode: notFound ? "Wallet not found" : (reasonCode ?? (status ? `HTTP_${status}` : "NETWORK_ERROR")),
    supportReference: detailSupportReference,
    status,
    capability,
    notFound,
  };
}

// --- Wallet activity (ALL-5458) ---

/**
 * Incoming funds for one wallet. Never throws: a failed or malformed answer is
 * reported as `monitoring: "UNAVAILABLE"` with no entries, so the page says the
 * feed is down instead of implying the wallet has received nothing.
 */
export async function fetchWalletActivity(id: string): Promise<WalletActivity> {
  const unavailable: WalletActivity = { walletId: id, monitoring: "UNAVAILABLE", asOf: "", activity: [] };
  try {
    const res = await apiFetch(`/v1/wallets/${encodeURIComponent(id)}/activity?limit=50`, {}, "wallet_activity");
    if (!res.ok) return unavailable;
    const data = await res.json();
    return normalizeWalletActivity(id, data);
  } catch {
    return unavailable;
  }
}

const ACTIVITY_STATUSES = new Set(["PENDING", "CONFIRMED", "REORGED"]);

export function normalizeWalletActivity(id: string, raw: unknown): WalletActivity {
  const data = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const monitoring = (data.monitoring as Record<string, unknown> | undefined)?.status;
  const status: WalletActivity["monitoring"] =
    monitoring === "WATCHING" || monitoring === "NOT_CONFIGURED" ? monitoring : "UNAVAILABLE";
  const rows = Array.isArray(data.activity) ? (data.activity as Record<string, unknown>[]) : [];
  const activity = rows
    .filter((r) => r && typeof r.transactionHash === "string" && ACTIVITY_STATUSES.has(String(r.status)))
    .map((r) => {
      const asset = (r.asset && typeof r.asset === "object" ? r.asset : {}) as Record<string, unknown>;
      const explorerUrl = typeof r.explorerUrl === "string" && r.explorerUrl.startsWith("https://") ? r.explorerUrl : null;
      return {
        id: String(r.id ?? r.transactionHash),
        direction: "INCOMING" as const,
        status: String(r.status) as WalletActivityEntry["status"],
        asset: {
          kind: asset.kind === "ERC20" ? ("ERC20" as const) : ("NATIVE" as const),
          symbol: String(asset.symbol ?? ""),
          contractAddress: typeof asset.contractAddress === "string" ? asset.contractAddress : null,
          decimals: Number(asset.decimals ?? 0),
        },
        amount: String(r.amount ?? "0"),
        amountAtomic: String(r.amountAtomic ?? "0"),
        from: String(r.from ?? ""),
        to: String(r.to ?? ""),
        transactionHash: String(r.transactionHash),
        explorerUrl,
        blockNumber: Number(r.blockNumber ?? 0),
        observedAt: String(r.observedAt ?? ""),
        confirmedAt: typeof r.confirmedAt === "string" ? r.confirmedAt : null,
        assetVerified: r.assetVerified === true,
      };
    });
  return { walletId: String(data.walletId ?? id), monitoring: status, asOf: String(data.asOf ?? ""), activity };
}

// --- Notifications ---

export async function fetchNotifications(): Promise<{ notifications: Record<string, unknown>[]; reasonCode?: string }> {
  try {
    const res = await apiFetch("/v1/notifications", {}, "notification_list");
    const data = await res.json();
    if (!res.ok) return { notifications: [], reasonCode: data?.reason_code || data?.reasonCode || `HTTP ${res.status}` };
    return { notifications: Array.isArray(data.notifications) ? data.notifications : Array.isArray(data) ? data : [] };
  } catch {
    return { notifications: [], reasonCode: "Network error" };
  }
}

export async function markNotificationRead(id: string): Promise<{ ok: boolean }> {
  try {
    const res = await apiFetch(`/v1/notifications/${encodeURIComponent(id)}/read`, { method: "POST" }, "notification_read");
    return { ok: res.ok };
  } catch {
    return { ok: false };
  }
}

/**
 * GET/PUT /v1/notifications/preferences — channel key is a backend event
 * type (transfer.submitted, approval.required, approval.resolved,
 * transfer.failed; see JdbcNotificationInboxService.ALLOWED_EVENT_TYPES in
 * alloy-api-gateway-service), each with independent email/inApp/webhook
 * booleans. This is NOT a flat list of named toggles — that shape does not
 * exist on the backend (ALL-4738 G1/G2).
 */
export interface NotificationChannelConfig {
  email: boolean;
  inApp: boolean;
  webhook: boolean;
}

export interface NotificationPreferences {
  tenantId: string;
  channels: Record<string, NotificationChannelConfig>;
}

export async function fetchNotificationPreferences(): Promise<ApiDetailResponse<NotificationPreferences>> {
  return fetchDetail<NotificationPreferences>("/v1/notifications/preferences", "notification_preferences_get");
}

export async function updateNotificationPreferences(
  channels: Record<string, NotificationChannelConfig>,
): Promise<ApiMutationResult> {
  return apiMutate("/v1/notifications/preferences", "PUT", { channels }, "notification_preferences_put");
}

// --- Config ---

/**
 * GET/PUT /v1/config/settings (TenantConfigDomain.SETTINGS) — the only
 * allow-listed keys are transfer.auto_approve_threshold,
 * transfer.default_currency, notifications.email_enabled and
 * session.idle_timeout_minutes (see TenantConfigSchema in
 * alloy-api-gateway-service). There is no backend concept of an
 * organization display name, legal name, primary domain, timezone,
 * country or org id — never invent those from this endpoint.
 */
export interface ConfigFieldMetadata {
  type: string;
  defaultValue: unknown;
  sensitivity: string;
  mutability: string;
  validation: string;
  owner: string;
}

export interface ConfigSettingsSnapshot {
  tenantId: string;
  settings: Record<string, unknown>;
  version: number;
  updatedAt: string;
  schema: Record<string, ConfigFieldMetadata>;
}

export async function fetchConfigSettings(): Promise<ApiDetailResponse<ConfigSettingsSnapshot>> {
  return fetchDetail<ConfigSettingsSnapshot>("/v1/config/settings", "config_settings_get");
}

export async function updateConfigSettings(
  settings: Record<string, unknown>,
  expectedVersion: number,
): Promise<ApiMutationResult> {
  return apiMutate("/v1/config/settings", "PUT", { settings, expectedVersion }, "config_settings_put");
}

/**
 * GET/PUT /v1/config/branding (TenantConfigDomain.BRANDING) — the only
 * allow-listed keys are logoUrl (a bounded https asset URL; there is no
 * file-upload endpoint), primaryColor (one of a fixed set of design
 * tokens) and companyName (1-80 printable characters).
 */
export interface ConfigBrandingSnapshot {
  tenantId: string;
  logoUrl: string | null;
  primaryColor: string | null;
  companyName: string | null;
  version: number;
  updatedAt: string;
  schema: Record<string, ConfigFieldMetadata>;
}

export async function fetchConfigBranding(): Promise<ApiDetailResponse<ConfigBrandingSnapshot>> {
  return fetchDetail<ConfigBrandingSnapshot>("/v1/config/branding", "config_branding_get");
}

/**
 * GatewayController#updateBranding always rewrites all three branding keys
 * together (ConfigController.java puts logoUrl/primaryColor/companyName into
 * one patch map regardless of which field changed), and
 * TenantConfigSchema#validateBranding rejects a null companyName or
 * primaryColor outright — so every PUT must resend the full triple, not a
 * partial patch. logoUrl may be null (no logo configured).
 */
export async function updateConfigBranding(
  branding: { logoUrl: string | null; primaryColor: string; companyName: string },
  expectedVersion: number,
): Promise<ApiMutationResult> {
  return apiMutate(
    "/v1/config/branding",
    "PUT",
    { ...branding, expectedVersion },
    "config_branding_put",
  );
}

// --- Capabilities ---

export async function fetchCapabilities(): Promise<{ capabilities: Record<string, unknown> | null; error?: string }> {
  try {
    const res = await apiFetch("/v1/capabilities", {}, "capabilities");
    const data = await res.json();
    if (!res.ok) return { capabilities: null, error: `HTTP ${res.status}` };
    return { capabilities: data };
  } catch {
    return { capabilities: null, error: "Network error" };
  }
}

// --- Admin Users ---

export interface AdminUser {
  principalId: string;
  principalType: string;
  displayName: string;
  email: string;
  status: string;
  createdAt: string;
  ownerPrincipalId?: string;
  roleName?: string;
  mfaMethod?: string;
  groupNames?: string;
}

/**
 * Admin endpoints serialize Instant fields as epoch seconds, while older
 * contract snapshots and some provider responses use ISO strings. Normalize
 * the wire value once at the API boundary so every admin date consumer gets
 * one stable ISO representation and never hands epoch seconds to Date as
 * milliseconds (which renders dates in 1970).
 */
function normalizeAdminTimestamp(value: unknown): string | null {
  if (value == null || value === "") return value == null ? null : "";
  if (typeof value === "number" && Number.isFinite(value)) {
    const milliseconds = Math.abs(value) < 100_000_000_000 ? value * 1000 : value;
    const date = new Date(milliseconds);
    return Number.isNaN(date.getTime()) ? String(value) : date.toISOString();
  }
  return typeof value === "string" ? value : String(value);
}

function normalizeAdminUser<T extends Record<string, unknown>>(user: T): T {
  if (!user || typeof user !== "object") return user;
  if (!Object.prototype.hasOwnProperty.call(user, "createdAt")) return user;
  return { ...user, createdAt: normalizeAdminTimestamp(user.createdAt) } as T;
}

function normalizeAdminEvent<T extends Record<string, unknown>>(event: T): T {
  if (!event || typeof event !== "object") return event;
  if (!Object.prototype.hasOwnProperty.call(event, "occurredAt")) return event;
  return { ...event, occurredAt: normalizeAdminTimestamp(event.occurredAt) } as T;
}

function normalizeAdminSession<T extends Record<string, unknown>>(session: T): T {
  if (!session || typeof session !== "object") return session;
  const normalized: Record<string, unknown> = { ...session };
  for (const key of ["createdAt", "lastSeenAt", "idleExpiresAt"]) {
    if (Object.prototype.hasOwnProperty.call(normalized, key)) {
      normalized[key] = normalizeAdminTimestamp(normalized[key]);
    }
  }
  return normalized as T;
}

function normalizeAdminInvite<T extends Record<string, unknown>>(invite: T): T {
  if (!invite || typeof invite !== "object") return invite;
  const normalized: Record<string, unknown> = { ...invite };
  for (const key of ["createdAt", "expiresAt"]) {
    if (Object.prototype.hasOwnProperty.call(normalized, key)) {
      normalized[key] = normalizeAdminTimestamp(normalized[key]);
    }
  }
  return normalized as T;
}

export interface AdminGroup {
  groupId: string;
  name: string;
  description: string;
  status: string;
  memberCount: number;
}

export interface AdminSession {
  sessionIdSuffix: string;
  authMethod: string;
  mfaPresent: boolean;
  assuranceLevel: string;
  channel: string;
  createdAt: string;
  lastSeenAt: string | null;
  idleExpiresAt: string | null;
}

export interface AuditEvent {
  eventId: string;
  eventType: string;
  outcome: string;
  authMethod: string;
  assuranceLevel: string;
  mfaPresent: boolean;
  occurredAt: string | null;
}

export interface AdminUsersResult {
  users: AdminUser[];
  total: number;
  error?: string;
}

export async function fetchAdminUsers(): Promise<AdminUsersResult> {
  try {
    const res = await apiFetch("/v1/admin/users", {}, "admin_users");
    if (!res.ok) return { users: [], total: 0, error: `HTTP ${res.status}` };
    const data = await res.json();
    return { users: Array.isArray(data.users) ? data.users.map(normalizeAdminUser) : [], total: data.total || 0 };
  } catch {
    return { users: [], total: 0, error: "Network error" };
  }
}

export async function fetchAdminUserDetail(userId: string): Promise<{ user: AdminUser | null; error?: string }> {
  try {
    const res = await apiFetch(`/v1/admin/users/${encodeURIComponent(userId)}`, {}, "admin_user_detail");
    if (!res.ok) return { user: null, error: `HTTP ${res.status}` };
    const data = await res.json();
    return { user: normalizeAdminUser(data) };
  } catch {
    return { user: null, error: "Network error" };
  }
}

export interface AdminAuditEvent {
  eventId: string;
  occurredAt: string;
  // actor/target are nullable on the real backend record (OrgAuditEventEntry
  // in AdminBootstrapLifecycleService.java): system-originated events like
  // API_KEY_VERIFICATION_ALLOWED have no target, and some events have no
  // actor. Verified live against dev.alloy.build (ALL-4729 G6).
  actor: string | null;
  actorType: string | null;
  action: string;
  resourceType: string;
  target: string | null;
  targetType: string | null;
  outcome: string;
  reasonCode: string;
  correlationId: string;
}

export interface AdminAuditLogResult extends ResponseSupport {
  events: AdminAuditEvent[];
  total: number;
  offset: number;
  limit: number;
  error?: string;
}

export async function fetchAuditEvents(params: {
  query?: string;
  action?: string;
  actor?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
} = {}): Promise<AdminAuditLogResult> {
  const qs = new URLSearchParams();
  if (params.query) qs.set("query", params.query);
  if (params.action) qs.set("action", params.action);
  if (params.actor) qs.set("actor", params.actor);
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.limit != null) qs.set("limit", String(params.limit));
  if (params.offset != null) qs.set("offset", String(params.offset));
  const q = qs.toString();
  try {
    const res = await apiFetch(`/v1/admin/audit${q ? `?${q}` : ""}`, {}, "admin_audit");
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      return { events: [], total: 0, offset: 0, limit: params.limit ?? 20, error: `HTTP ${res.status}`, ...responseSupport(res, data) };
    }
    const data = await res.json();
    return {
      events: Array.isArray(data.events) ? data.events.map(normalizeAdminEvent) : [],
      total: typeof data.total === "number" ? data.total : 0,
      offset: typeof data.offset === "number" ? data.offset : 0,
      limit: typeof data.limit === "number" ? data.limit : (params.limit ?? 20),
    };
  } catch {
    return { events: [], total: 0, offset: 0, limit: params.limit ?? 20, error: "Network error" };
  }
}

export interface AdminInvite {
  inviteId: string;
  email: string;
  displayName: string;
  roleName: string;
  status: string;
  deliveryStatus: string;
  expiresAt: string;
  createdAt: string;
  actions?: { canResend: boolean; canRevoke: boolean };
}

export async function fetchAdminInvites(): Promise<{ invites: AdminInvite[]; error?: string }> {
  try {
    const res = await apiFetch("/v1/admin/invites", {}, "admin_invites");
    if (!res.ok) return { invites: [], error: `HTTP ${res.status}` };
    const data = await res.json();
    return { invites: Array.isArray(data.invites) ? data.invites.map(normalizeAdminInvite) : [] };
  } catch {
    return { invites: [], error: "Network error" };
  }
}

export interface AdminInviteRequest {
  email: string;
  displayName: string;
  roleName: string;
  mfaRequirement?: string;
  environmentScope?: string;
  expiresInHours?: number;
}

export interface AdminInviteResult extends ResponseSupport {
  ok: boolean;
  inviteId?: string;
  error?: string;
  status?: number;
  /** Backend reason code (BoundedAuthError.reasonCode), e.g. AUTH_STEP_UP_REQUIRED. */
  reasonCode?: string;
}

export async function createAdminInvite(
  invite: AdminInviteRequest,
  callerKey?: string,
): Promise<AdminInviteResult> {
  try {
    // ALL-5312: the gateway requires Idempotency-Key on this route and the auth service
    // replays the original invite for the same key, so the header is always sent. A
    // step-up resume passes the same key both attempts (lib/step-up-resume.ts,
    // ALL-5022, ALL-5285); otherwise each call gets a fresh key.
    const key = callerKey || idempotencyKey("admin-invite");
    const res = await apiFetch("/v1/admin/invites", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Idempotency-Key": key },
      body: JSON.stringify(invite),
    }, "admin_invite_create");
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      // RFC 9457 problem bodies carry the human-readable message in `detail`,
      // not `message`; prefer it so the wizard shows the refusal the gateway
      // actually wrote, and keep `message`/`reasonCode` as legacy fallbacks.
      return {
        ok: false,
        status: res.status,
        error: data?.detail || data?.message || data?.reasonCode || "Invite failed",
        reasonCode: responseReasonCode(data),
        ...responseSupport(res, data),
      };
    }
    return { ok: true, inviteId: data.inviteId, ...responseSupport(res, data) };
  } catch {
    return { ok: false, error: "Network error" };
  }
}

export interface AdminSuspendResult extends ResponseSupport {
  ok: boolean;
  error?: string;
  /** Backend reason code (BoundedAuthError.reasonCode), e.g. AUTH_STEP_UP_REQUIRED. */
  reasonCode?: string;
  httpStatus?: number;
}

export async function suspendAdminUser(
  userId: string,
  callerKey?: string,
): Promise<AdminSuspendResult> {
  try {
    // ALL-5312: required by the gateway; one key per user action (see createAdminInvite).
    // A step-up resume passes the same key both attempts (ALL-5285); otherwise a fresh one.
    const key = callerKey || idempotencyKey("admin-suspend");
    const res = await apiFetch(`/v1/admin/users/${encodeURIComponent(userId)}/suspend`, {
      method: "POST",
      headers: { "Idempotency-Key": key },
    }, "admin_user_suspend");
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const body = (data ?? {}) as Record<string, unknown>;
      return {
        ok: false,
        error: boundedErrorText(body.detail) || boundedErrorText(body.message) || "Suspend failed",
        reasonCode: responseReasonCode(body),
        httpStatus: res.status,
        ...responseSupport(res, data),
      };
    }
    return { ok: true, ...responseSupport(res, data) };
  } catch {
    return { ok: false, error: "Network error" };
  }
}

export interface AdminRoleUpdateResult extends ResponseSupport {
  ok: boolean;
  roles?: string[];
  effectiveOn?: string;
  error?: string;
  /** Backend reason code (BoundedAuthError.reasonCode), e.g. AUTH_STEP_UP_REQUIRED. */
  reasonCode?: string;
  httpStatus?: number;
}

export interface AdminRoleUpdateRequest {
  userId: string;
  roles: string[];
  reason?: string;
}

export async function updateAdminUserRoles(
  userId: string,
  roles: string[],
  reason?: string,
  idempotencyKey?: string,
): Promise<AdminRoleUpdateResult> {
  try {
    // This route IS in the gateway's required set
    // (GatewayRouteScopes.requiresIdempotency: PATCH /v1/admin/users/{id}/roles),
    // so the key must stay inside the gateway's charset and — when a step-up
    // resume replays the change — must be the same value both times (ALL-5022).
    const key = idempotencyKey || `admin-role-${newIdempotencyKey()}`;
    const res = await apiFetch(`/v1/admin/users/${encodeURIComponent(userId)}/roles`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "Idempotency-Key": key,
      },
      body: JSON.stringify({ roles, reason }),
    }, "admin_user_roles_update");
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const body = (data ?? {}) as Record<string, unknown>;
      return {
        ok: false,
        error: boundedErrorText(body.detail)
          || boundedErrorText(body.message)
          || responseReasonCode(body)
          || "Role update failed",
        reasonCode: responseReasonCode(body),
        httpStatus: res.status,
        ...responseSupport(res, data),
      };
    }
    const result = data as Record<string, unknown>;
    return {
      ok: true,
      roles: Array.isArray(result.roles) ? result.roles.map(String) : roles,
      effectiveOn: typeof result.effectiveOn === "string" ? result.effectiveOn : "NEXT_SIGN_IN",
      ...responseSupport(res, data),
    };
  } catch {
    return { ok: false, error: "Network error" };
  }
}

export interface SignerPairingResult extends ResponseSupport {
  ok: boolean;
  pairingId?: string;
  /** 10-char Crockford base32 code (MOBILE-SIGNING-CONTRACT.md ALL-4884 §"Identity and auth").
   * Single-use, expires at `expiresAt`; never persisted client-side beyond this response. */
  pairingCode?: string;
  expiresAt?: string;
  error?: string;
  /** Backend reason code, e.g. PRINCIPAL_NOT_HUMAN or an AUTH_STEP_UP_* code. */
  reasonCode?: string;
  httpStatus?: number;
}

/**
 * POST /v1/signer/pairings (ALL-4884 mobile-signing lane, gateway route to
 * alloy-signing-relay-service). Browser-session + AAL2 step-up call; the
 * caller wraps this in withStepUpRetry the same way approve/reject do
 * (ALL-5022). The backend refuses a machine/service principalId with
 * PRINCIPAL_NOT_HUMAN — the UI never lets that happen (see
 * isHumanPrincipal() in pages/admin/user-detail.tsx) but the server check is
 * the real enforcement point either way.
 */
export async function createSignerPairing(principalId: string): Promise<SignerPairingResult> {
  try {
    const res = await apiFetch("/v1/signer/pairings", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ principalId }),
    }, "signer_pairing_create");
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const body = (data ?? {}) as Record<string, unknown>;
      return {
        ok: false,
        error: boundedErrorText(body.message)
          || responseReasonCode(body)
          || "Pairing request failed",
        reasonCode: responseReasonCode(body),
        httpStatus: res.status,
        ...responseSupport(res, data),
      };
    }
    const body = data as Record<string, unknown>;
    return {
      ok: true,
      pairingId: typeof body.pairingId === "string" ? body.pairingId : undefined,
      pairingCode: typeof body.pairingCode === "string" ? body.pairingCode : undefined,
      expiresAt: typeof body.expiresAt === "string" ? body.expiresAt : undefined,
      ...responseSupport(res, data),
    };
  } catch {
    return { ok: false, error: "Network error" };
  }
}

/**
 * POST /v1/signer/pairings/self (D714, ALL-5472). An invited signer creates a
 * pairing code for THEIR OWN first phone, with no admin step. No body and no
 * principal parameter: the gateway binds the code to the signed-in principal.
 * Browser-session + AAL2 step-up, so callers wrap it in withStepUpRetry.
 * Refusals the UI explains: SIGNER_DEVICE_ALREADY_BOUND (a replacement phone
 * needs the two-admin re-bind), SELF_PAIRING_DISABLED (the org requires an
 * admin-issued code) and SIGNER_ROLE_REQUIRED.
 */
export async function createSelfSignerPairing(): Promise<SignerPairingResult> {
  try {
    const res = await apiFetch("/v1/signer/pairings/self", {
      method: "POST",
    }, "signer_pairing_self_create");
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const body = (data ?? {}) as Record<string, unknown>;
      return {
        ok: false,
        error: boundedErrorText(body.detail)
          || boundedErrorText(body.message)
          || responseReasonCode(body)
          || "Pairing request failed",
        reasonCode: responseReasonCode(body),
        httpStatus: res.status,
        ...responseSupport(res, data),
      };
    }
    const body = data as Record<string, unknown>;
    return {
      ok: true,
      pairingId: typeof body.pairingId === "string" ? body.pairingId : undefined,
      pairingCode: typeof body.pairingCode === "string" ? body.pairingCode : undefined,
      expiresAt: typeof body.expiresAt === "string" ? body.expiresAt : undefined,
      ...responseSupport(res, data),
    };
  } catch {
    return { ok: false, error: "Network error" };
  }
}

/**
 * Reason codes that mean "this action needs a fresh step-up proof on the
 * session" (GatewayBrowserSessionAuthenticationFilter.enforceStepUpIfRequired
 * and AuthContextValidator.requireMinimumAssurance). Kept in step with the
 * step_up_required rows of i18n/error-categories.ts.
 */
export function isStepUpReasonCode(reasonCode: string | undefined | null): boolean {
  return categoryForReasonCode(reasonCode ?? undefined) === "step_up_required";
}

export interface StepUpResult extends ResponseSupport {
  ok: boolean;
  error?: string;
  reasonCode?: string;
  httpStatus?: number;
}

export interface StepUpChallengeResult extends StepUpResult {
  challengeId?: string;
  nonce?: string;
  /** When the challenge stops being completable (AuthStepUpChallengeResult.expiresAt);
   * a pending resume is discarded at this instant rather than replayed. */
  expiresAt?: string;
}

/** POST /v1/step-up/challenges (GatewayStepUpController#createChallenge). */
export async function createStepUpChallenge(): Promise<StepUpChallengeResult> {
  try {
    const res = await apiFetch("/v1/step-up/challenges", { method: "POST" }, "step_up_challenge");
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) {
      return {
        ok: false,
        error: boundedErrorText(body.message) || `HTTP ${res.status}`,
        reasonCode: responseReasonCode(body),
        httpStatus: res.status,
        ...responseSupport(res, body),
      };
    }
    const challengeId = typeof body.challengeId === "string" ? body.challengeId : "";
    const nonce = typeof body.nonce === "string" ? body.nonce : "";
    if (!challengeId || !nonce) {
      return { ok: false, error: "Step-up challenge was incomplete", ...responseSupport(res, body) };
    }
    return {
      ok: true,
      challengeId,
      nonce,
      expiresAt: typeof body.expiresAt === "string" ? body.expiresAt : undefined,
      ...responseSupport(res, body),
    };
  } catch {
    return { ok: false, error: "Network error" };
  }
}

/**
 * POST /v1/step-up/challenges/{id}/complete with the challenge's own nonce
 * (GatewayStepUpController#completeChallenge). Auth issues the receipt only
 * when the session's own assurance already meets the required level, so
 * this never raises assurance by itself; a stale/AAL1 session gets
 * AUTH_ASSURANCE_INSUFFICIENT or AUTH_STEP_UP_EXPIRED back (see
 * isStepUpReasonCode) and there is nothing more this call alone can do about
 * it -- the caller needs a fresh IdP round trip (ALL-5021's
 * step_up_challenge_id login intent, once it lands) or a plain sign-out.
 */
export async function completeStepUpChallenge(challengeId: string, nonce: string): Promise<StepUpResult> {
  try {
    const res = await apiFetch(
      `/v1/step-up/challenges/${encodeURIComponent(challengeId)}/complete`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rawNonce: nonce }),
      },
      "step_up_complete",
    );
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) {
      return {
        ok: false,
        // RFC 9457 problem bodies carry the human-readable text in `detail`, not
        // `message`; prefer it so the refusal the gateway actually wrote is what
        // a resumed action shows alongside its support reference.
        error: boundedErrorText(body.detail) || boundedErrorText(body.message) || `HTTP ${res.status}`,
        reasonCode: responseReasonCode(body),
        httpStatus: res.status,
        ...responseSupport(res, body),
      };
    }
    return { ok: true, ...responseSupport(res, body) };
  } catch {
    return { ok: false, error: "Network error" };
  }
}

export interface BrowserStepUpResult extends StepUpResult {
  /** The challenge this attempt used, carried back so a caller that has to send
   * the browser through a step-up re-authentication (ALL-5021) resumes *that*
   * challenge instead of minting a second one. */
  challengeId?: string;
  nonce?: string;
  expiresAt?: string;
}

/**
 * Runs the gateway browser step-up end to end on the current session: create a
 * challenge, then complete it. Kept as a thin wrapper over
 * createStepUpChallenge/completeStepUpChallenge (ALL-5022).
 */
export async function completeBrowserStepUp(): Promise<BrowserStepUpResult> {
  const challenge = await createStepUpChallenge();
  if (!challenge.ok || !challenge.challengeId || !challenge.nonce) {
    return challenge;
  }
  const completion = await completeStepUpChallenge(challenge.challengeId, challenge.nonce);
  return {
    ...completion,
    challengeId: challenge.challengeId,
    nonce: challenge.nonce,
    expiresAt: challenge.expiresAt,
  };
}

/**
 * The two reason codes that mean the *session* cannot satisfy step-up however
 * often the challenge is completed: the assurance it carries is below the
 * required level, or the sign-in it is based on is too old
 * (AuthStepUpService#requireAssurance). Only a fresh IdP round trip raises
 * either, which is what ALL-5021's `step_up_challenge_id` login intent is for.
 */
export const STEP_UP_REAUTH_REASON_CODES = ["AUTH_ASSURANCE_INSUFFICIENT", "AUTH_STEP_UP_EXPIRED"] as const;

/** The action itself refused the session's assurance level (AuthContextValidator
 * #requireMinimumAssurance), which no in-session step-up challenge can raise. */
function sessionAssuranceTooLow(reasonCode: string | undefined | null): boolean {
  return (reasonCode ?? "").trim() === "AUTH_ASSURANCE_INSUFFICIENT";
}

export function requiresStepUpReauth(reasonCode: string | undefined | null): boolean {
  return STEP_UP_REAUTH_REASON_CODES.includes(
    (reasonCode ?? "").trim() as (typeof STEP_UP_REAUTH_REASON_CODES)[number],
  );
}

export interface StepUpReauthStart {
  ok: boolean;
  /** Same-origin path to navigate to, e.g. `/auth/login?return_to=…&step_up_challenge_id=…`. */
  path?: string;
  error?: string;
  supportReference?: string;
}

/**
 * Builds the step-up re-authentication URL for an existing challenge.
 *
 * The provider comes from the same /auth/options call the login page uses —
 * never a hardcoded one — because the round trip has to go through the identity
 * provider this organization's session is bound to; the gateway refuses a
 * provider-less login outright when the org has more than one usable provider
 * (`GatewayBrowserOAuthController#resolveBrowserProvider`). `return_to` is set
 * explicitly so the IdP sends the browser back to the page whose resume hook
 * will complete the challenge and replay the request.
 */
export async function startStepUpReauthentication(
  challengeId: string,
  returnPath: string,
): Promise<StepUpReauthStart> {
  if (!isSafeReturnPath(returnPath)) {
    return { ok: false, error: "The page to return to could not be determined." };
  }
  const methods = (await fetchServedAuthOptions(returnPath)) ?? [];
  const method = methods.find((candidate) => candidate.enabled && candidate.primary)
    ?? methods.find((candidate) => candidate.enabled);
  if (!method || !method.startUrl) {
    return { ok: false, error: "No sign-in method is available for this organization." };
  }
  try {
    const url = new URL(method.startUrl, window.location.origin);
    url.searchParams.set("return_to", returnPath);
    url.searchParams.set("step_up_challenge_id", challengeId);
    return { ok: true, path: `${url.pathname}${url.search}` };
  } catch {
    return { ok: false, error: "The sign-in redirect could not be built." };
  }
}

/**
 * The outcome of wrapping a mutation in a step-up retry: `result` is the
 * final attempt's response (the original 403 if no step-up was needed or
 * possible, or the retried mutation's response if step-up succeeded).
 * `stepUpFailed` is true only when step-up was attempted and itself failed
 * (e.g. AUTH_ASSURANCE_INSUFFICIENT/AUTH_STEP_UP_EXPIRED) -- callers should
 * show a distinct "needs a recent MFA sign-in" message in that case rather
 * than the original mutation's raw reason code.
 */
export interface StepUpRetryOutcome<T> {
  result: T;
  stepUpAttempted: boolean;
  stepUpFailed: boolean;
  /** True when the browser is being navigated to a step-up re-authentication
   * (ALL-5021) and the caller's request will be replayed on the way back by the
   * resume hook. Nothing further happens in this page: no error to render. */
  redirected: boolean;
  stepUpReasonCode?: string;
  stepUpSupportReference?: string;
}

/**
 * Everything the resume needs to re-issue the same write after the user comes
 * back from a step-up re-authentication.
 */
export interface StepUpResumeDescriptor {
  /** Operation descriptor; must name an entry of STEP_UP_RESUME_ACTIONS. */
  action: StepUpResumeActionId;
  /** Exactly the arguments that operation needs, so the replay is the same
   * request and not a similar one. */
  payload: unknown;
  /** The idempotency key makeRequest already used for its first attempt. The
   * replay sends this same value, so the gateway sees one write. */
  idempotencyKey: string;
  /** Same-origin page the IdP round trip returns to; that page mounts the
   * resume hook. */
  returnPath: string;
}

/**
 * Wraps a mutating call (approve/reject, invite, suspend, role change, or
 * anything else shaped like { ok, reasonCode }): on a step-up-required reason
 * code, run the gateway browser step-up once and, only if it succeeds, call
 * `makeRequest` again. `makeRequest` must return the SAME idempotency key on
 * both the first and retried call (bake it into the closure) so the gateway
 * treats the retry as the same decision, not a second one. No dialog is shown
 * here -- callers decide their own loading/denied UI; this never invents an
 * MFA/passkey input, because nothing on the backend accepts one
 * (ALL-5013/ALL-5022).
 *
 * Passing `resume` closes the hole that used to leave the user stuck: when the
 * in-place completion itself answers AUTH_ASSURANCE_INSUFFICIENT or
 * AUTH_STEP_UP_EXPIRED, this session can never satisfy step-up on its own, so
 * the challenge is persisted with the request it belongs to and the browser is
 * sent through a step-up re-authentication (ALL-5021). The page named by
 * `returnPath` completes that same challenge on the way back and replays the
 * request exactly once (useStepUpResume). Without `resume` -- approve, reject,
 * SSO domains, signer pairing keep the previous behaviour -- no redirect is
 * started, which is the safe default: an unresumable redirect would drop the
 * user's write silently.
 */
export async function withStepUpRetry<T extends { ok: boolean; reasonCode?: string }>(
  makeRequest: () => Promise<T>,
  resume?: StepUpResumeDescriptor,
): Promise<StepUpRetryOutcome<T>> {
  const first = await makeRequest();
  if (first.ok || !isStepUpReasonCode(first.reasonCode)) {
    return { result: first, stepUpAttempted: false, stepUpFailed: false, redirected: false };
  }
  if (!resume && sessionAssuranceTooLow(first.reasonCode)) {
    // The action refused the SESSION's assurance, not a missing or stale step-up
    // receipt. A challenge cannot raise it: auth refuses completion on a session
    // below the required level (AuthStepUpService#requireAssurance), and the gateway
    // refuses the challenge outright while step-up is off (AUTH_ASSURANCE_NOT_ALLOWED,
    // ALL-5454). Only a re-authentication helps, and without `resume` none can be
    // started, so challenging here was a futile second refusal whose reference hid
    // the action's own. Return the action's refusal unchanged; the server still
    // enforces the same assurance on every retry.
    return { result: first, stepUpAttempted: false, stepUpFailed: false, redirected: false };
  }
  const stepUp = await completeBrowserStepUp();
  if (!stepUp.ok) {
    if (resume && requiresStepUpReauth(stepUp.reasonCode) && stepUp.challengeId && stepUp.nonce) {
      const started = await startStepUpReauthentication(stepUp.challengeId, resume.returnPath);
      if (started.ok && started.path) {
        savePendingStepUpResume({
          action: resume.action,
          payload: resume.payload,
          idempotencyKey: resume.idempotencyKey,
          challengeId: stepUp.challengeId,
          nonce: stepUp.nonce,
          returnPath: resume.returnPath,
          // The earlier of the challenge's own expiry and the configured bound,
          // so the stored record never outlives the challenge it carries.
          expiresAt: pendingResumeExpiry(stepUp.expiresAt),
        });
        redirectToStepUpLogin(started.path);
        return {
          result: first,
          stepUpAttempted: true,
          stepUpFailed: false,
          redirected: true,
          stepUpReasonCode: stepUp.reasonCode,
          stepUpSupportReference: stepUp.supportReference,
        };
      }
      return {
        result: first,
        stepUpAttempted: true,
        stepUpFailed: true,
        redirected: false,
        stepUpReasonCode: stepUp.reasonCode,
        stepUpSupportReference: started.supportReference ?? stepUp.supportReference,
      };
    }
    return {
      result: first,
      stepUpAttempted: true,
      stepUpFailed: true,
      redirected: false,
      stepUpReasonCode: stepUp.reasonCode,
      stepUpSupportReference: stepUp.supportReference,
    };
  }
  const retried = await makeRequest();
  return { result: retried, stepUpAttempted: true, stepUpFailed: false, redirected: false };
}

/** When a pending resume stops being usable: the challenge's own expiry, capped
 * at now + VITE_STEP_UP_RESUME_TTL_MINUTES (lib/step-up-resume.ts). */
export function pendingResumeExpiry(challengeExpiresAt: string | undefined, now = Date.now()): string {
  const bound = now + STEP_UP_RESUME_FALLBACK_TTL_MS;
  const challenge = challengeExpiresAt ? Date.parse(challengeExpiresAt) : Number.NaN;
  if (challengeExpiresAt && Number.isFinite(challenge) && challenge <= bound) return challengeExpiresAt;
  return new Date(bound).toISOString();
}

// --- Access requests (ALL-5723, specs/access-requests S-6..S-9) ---
//
// GET/POST /v1/admin/access-requests/** (AccessRequestController in the gateway). Reads need
// admin:users:read. Approve and deny need a human principal, bootstrap:admin:invite and a fresh
// AAL2 step-up, which the gateway enforces itself whatever its step-up switch says (S-8a); a
// session that is not fresh gets 403 AUTH_STEP_UP_REQUIRED and the page runs the existing
// step-up flow (withStepUpRetry + useStepUpResume). Both writes carry an Idempotency-Key and
// the CSRF header (apiFetchWithCorrelation adds it to every non-GET call).

export type AccessRequestStatus = "OPEN" | "APPROVED" | "DENIED" | "EXPIRED";

export const ACCESS_REQUEST_STATUSES: readonly AccessRequestStatus[] = ["OPEN", "APPROVED", "DENIED", "EXPIRED"];

/** RecommendationView: one recommendation with who made it. `principalType` says whether a
 * person or a machine principal (an agent or API key) wrote it. */
export interface AccessRequestRecommendation {
  recommendation: string;
  roleName?: string | null;
  rationale?: string | null;
  principalId?: string | null;
  principalType?: string | null;
  createdAt?: string | null;
}

/** AccessRequestView (gateway) / AccessRequest (openapi). */
export interface AccessRequest {
  requestId: string;
  email: string;
  displayName?: string | null;
  identityProviderId?: string | null;
  status: string;
  attemptCount: number;
  firstSeenAt: string;
  lastSeenAt: string;
  expiresAt: string;
  supportReference?: string | null;
  decidedByPrincipalId?: string | null;
  decidedAt?: string | null;
  decisionReason?: string | null;
  grantedRoleName?: string | null;
  inviteId?: string | null;
  recommendations?: AccessRequestRecommendation[];
}

export interface AccessRequestListResult extends ResponseSupport {
  items: AccessRequest[];
  nextCursor: string | null;
  error?: ApiErrorInfo;
  httpStatus?: number;
}

export interface AccessRequestDetailResult {
  item: AccessRequest | null;
  error?: ApiErrorInfo;
  httpStatus?: number;
}

export interface AccessRequestDecisionResult extends ResponseSupport {
  ok: boolean;
  status?: string;
  inviteId?: string;
  inviteStatus?: string;
  error?: string;
  reasonCode?: string;
  httpStatus?: number;
}

export interface AccessRequestApproveBody {
  roleName: string;
  reason: string;
}

/** The gateway's request-id pattern (AccessRequestController.REQUEST_ID). */
const ACCESS_REQUEST_ID = /^[A-Za-z0-9_-]{1,64}$/;

export function isAccessRequestId(value: unknown): value is string {
  return typeof value === "string" && ACCESS_REQUEST_ID.test(value);
}

function accessRequestFrom(raw: unknown): AccessRequest | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.requestId !== "string" || typeof r.email !== "string" || typeof r.status !== "string") return null;
  const text = (value: unknown) => (typeof value === "string" ? value : null);
  const recommendations: AccessRequestRecommendation[] = Array.isArray(r.recommendations)
    ? r.recommendations
      .filter((item): item is Record<string, unknown> => !!item && typeof item === "object")
      .filter((item) => typeof item.recommendation === "string")
      .map((item) => ({
        recommendation: item.recommendation as string,
        roleName: text(item.roleName),
        rationale: text(item.rationale),
        principalId: text(item.principalId),
        principalType: text(item.principalType),
        createdAt: text(item.createdAt),
      }))
    : [];
  return {
    requestId: r.requestId,
    email: r.email,
    displayName: text(r.displayName),
    identityProviderId: text(r.identityProviderId),
    status: r.status,
    attemptCount: typeof r.attemptCount === "number" ? r.attemptCount : 0,
    firstSeenAt: text(r.firstSeenAt) ?? "",
    lastSeenAt: text(r.lastSeenAt) ?? "",
    expiresAt: text(r.expiresAt) ?? "",
    supportReference: text(r.supportReference),
    decidedByPrincipalId: text(r.decidedByPrincipalId),
    decidedAt: text(r.decidedAt),
    decisionReason: text(r.decisionReason),
    grantedRoleName: text(r.grantedRoleName),
    inviteId: text(r.inviteId),
    recommendations,
  };
}

/** GET /v1/admin/access-requests?status=&cursor=&limit= (own organization only). */
export async function fetchAccessRequests(params: {
  status?: AccessRequestStatus;
  cursor?: string | null;
  limit?: number;
} = {}): Promise<AccessRequestListResult> {
  const query = new URLSearchParams();
  if (params.status) query.set("status", params.status);
  if (params.cursor) query.set("cursor", params.cursor);
  if (params.limit) query.set("limit", String(params.limit));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  try {
    const { response: res } = await apiFetchWithCorrelation(`/v1/admin/access-requests${suffix}`, {}, "access_requests_list");
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const body = data && typeof data === "object" ? data as Record<string, unknown> : {};
      return { items: [], nextCursor: null, error: extractApiError(res, body), httpStatus: res.status };
    }
    const body = (data ?? {}) as Record<string, unknown>;
    const items = Array.isArray(body.items)
      ? body.items.map(accessRequestFrom).filter((item): item is AccessRequest => item !== null)
      : [];
    return {
      items,
      nextCursor: typeof body.nextCursor === "string" && body.nextCursor ? body.nextCursor : null,
      ...responseSupport(res, data),
    };
  } catch {
    return { items: [], nextCursor: null, error: { reasonCode: "NETWORK_ERROR" } };
  }
}

/** GET /v1/admin/access-requests/count?status=OPEN, for the nav badge. `null` when it could not be read. */
export async function fetchAccessRequestCount(status: AccessRequestStatus = "OPEN"): Promise<number | null> {
  try {
    const res = await apiFetch(`/v1/admin/access-requests/count?status=${status}`, {}, "access_requests_count");
    if (!res.ok) return null;
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    return typeof data.count === "number" && Number.isFinite(data.count) && data.count >= 0 ? data.count : null;
  } catch {
    return null;
  }
}

/** GET /v1/admin/access-requests/{id}: one request with its recommendations. */
export async function fetchAccessRequest(requestId: string): Promise<AccessRequestDetailResult> {
  if (!isAccessRequestId(requestId)) {
    return { item: null, error: { reasonCode: "NOT_FOUND" }, httpStatus: 404 };
  }
  try {
    const { response: res } = await apiFetchWithCorrelation(
      `/v1/admin/access-requests/${encodeURIComponent(requestId)}`,
      {},
      "access_request_detail",
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const body = data && typeof data === "object" ? data as Record<string, unknown> : {};
      return { item: null, error: extractApiError(res, body), httpStatus: res.status };
    }
    const item = accessRequestFrom(data);
    return item ? { item } : { item: null, error: { reasonCode: "INVALID_RESPONSE", ...responseSupport(res, data) } };
  } catch {
    return { item: null, error: { reasonCode: "NETWORK_ERROR" } };
  }
}

async function postAccessRequestDecision(
  requestId: string,
  decision: "approve" | "deny",
  body: Record<string, string>,
  key: string,
): Promise<AccessRequestDecisionResult> {
  if (!isAccessRequestId(requestId)) return { ok: false, error: "Unknown access request" };
  try {
    const res = await apiFetch(`/v1/admin/access-requests/${encodeURIComponent(requestId)}/${decision}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Idempotency-Key": key },
      body: JSON.stringify(body),
    }, `access_request_${decision}`);
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) {
      return {
        ok: false,
        error: boundedErrorText(data.detail) || boundedErrorText(data.message) || responseReasonCode(data) || `HTTP ${res.status}`,
        reasonCode: responseReasonCode(data),
        httpStatus: res.status,
        ...responseSupport(res, data),
      };
    }
    return {
      ok: true,
      status: typeof data.status === "string" ? data.status : undefined,
      inviteId: typeof data.inviteId === "string" ? data.inviteId : undefined,
      inviteStatus: typeof data.inviteStatus === "string" ? data.inviteStatus : undefined,
      ...responseSupport(res, data),
    };
  } catch {
    return { ok: false, error: "Network error" };
  }
}

/** POST /v1/admin/access-requests/{id}/approve {roleName, reason}: issues an ordinary admin invite.
 * A step-up resume passes the same key both attempts, so the gateway sees one approval. */
export function approveAccessRequest(
  requestId: string,
  body: AccessRequestApproveBody,
  callerKey?: string,
): Promise<AccessRequestDecisionResult> {
  const key = callerKey || idempotencyKey("access-request-approve");
  return postAccessRequestDecision(requestId, "approve", { roleName: body.roleName, reason: body.reason }, key);
}

/** POST /v1/admin/access-requests/{id}/deny {reason}: no email is sent. */
export function denyAccessRequest(
  requestId: string,
  reason: string,
  callerKey?: string,
): Promise<AccessRequestDecisionResult> {
  const key = callerKey || idempotencyKey("access-request-deny");
  return postAccessRequestDecision(requestId, "deny", { reason }, key);
}

/** Operations a step-up resume may re-issue. A closed union on purpose: a record
 * naming anything else is discarded, never dispatched (see useStepUpResume). */
export type StepUpResumeActionId =
  | "admin.invite.create"
  | "admin.user.suspend"
  | "admin.user.roles.update"
  | "admin.access_request.approve"
  | "admin.access_request.deny";

export type StepUpResumeOutcome = { ok: boolean; error?: string; reasonCode?: string } & ResponseSupport;

function objectField(payload: unknown, field: string): string | null {
  if (!payload || typeof payload !== "object") return null;
  const value = (payload as Record<string, unknown>)[field];
  return typeof value === "string" && value.trim() ? value : null;
}

/** Rebuilds an invite request from a stored payload using only the fields the
 * invite contract accepts, each type-checked: anything else a same-origin script
 * may have written into the record is dropped, never forwarded to the gateway. */
function inviteFromPayload(payload: unknown): AdminInviteRequest | null {
  const email = objectField(payload, "email");
  const displayName = objectField(payload, "displayName");
  const roleName = objectField(payload, "roleName");
  if (!email || !displayName || !roleName) return null;
  const raw = payload as Record<string, unknown>;
  const invite: AdminInviteRequest = { email, displayName, roleName };
  const mfaRequirement = objectField(payload, "mfaRequirement");
  if (mfaRequirement) invite.mfaRequirement = mfaRequirement;
  const environmentScope = objectField(payload, "environmentScope");
  if (environmentScope) invite.environmentScope = environmentScope;
  if (typeof raw.expiresInHours === "number" && Number.isInteger(raw.expiresInHours) && raw.expiresInHours > 0) {
    invite.expiresInHours = raw.expiresInHours;
  }
  return invite;
}

/**
 * Re-issues a resumed request, keyed by the action persisted with the pending
 * record. This is the only place a resume turns into a request, and every entry
 * takes the record's own idempotency key so the replay is the same write rather
 * than a second one. Payloads that no longer type-check return
 * `{ ok: false }` with no text: nothing is sent, and the caller falls back to
 * the catalogue's generic resume-failure copy.
 */
export const STEP_UP_RESUME_ACTIONS: Record<
  StepUpResumeActionId,
  (payload: unknown, idempotencyKey: string) => Promise<StepUpResumeOutcome>
> = {
  "admin.invite.create": (payload, idempotencyKey) => {
    const invite = inviteFromPayload(payload);
    return invite ? createAdminInvite(invite, idempotencyKey) : Promise.resolve({ ok: false });
  },
  "admin.user.suspend": (payload, idempotencyKey) => {
    const userId = objectField(payload, "userId");
    return userId ? suspendAdminUser(userId, idempotencyKey) : Promise.resolve({ ok: false });
  },
  "admin.user.roles.update": (payload, idempotencyKey) => {
    const userId = objectField(payload, "userId");
    const roles = payload && typeof payload === "object"
      ? (payload as Record<string, unknown>).roles
      : null;
    if (
      !userId
      || !Array.isArray(roles)
      || roles.length === 0
      || !roles.every((role) => typeof role === "string" && role.trim().length > 0)
    ) {
      return Promise.resolve({ ok: false });
    }
    const reason = payload && typeof payload === "object"
      ? (payload as Record<string, unknown>).reason
      : undefined;
    return updateAdminUserRoles(
      userId,
      roles.map(String),
      typeof reason === "string" ? reason : undefined,
      idempotencyKey,
    );
  },
  // Rebuilt from the type-checked fields only; anything else in the stored record (the
  // requester's email, kept for the success message) is never sent.
  "admin.access_request.approve": (payload, idempotencyKey) => {
    const requestId = objectField(payload, "requestId");
    const roleName = objectField(payload, "roleName");
    const reason = objectField(payload, "reason");
    if (!isAccessRequestId(requestId) || !roleName || !reason) return Promise.resolve({ ok: false });
    return approveAccessRequest(requestId, { roleName, reason }, idempotencyKey);
  },
  "admin.access_request.deny": (payload, idempotencyKey) => {
    const requestId = objectField(payload, "requestId");
    const reason = objectField(payload, "reason");
    if (!isAccessRequestId(requestId) || !reason) return Promise.resolve({ ok: false });
    return denyAccessRequest(requestId, reason, idempotencyKey);
  },
};

/** True when `action` names an operation a resume may replay. */
export function isStepUpResumeActionId(action: string): action is StepUpResumeActionId {
  return Object.prototype.hasOwnProperty.call(STEP_UP_RESUME_ACTIONS, action);
}

/**
 * Completes the challenge a pending record carries and re-issues its request
 * exactly once with the record's idempotency key. Called only by
 * useStepUpResume, which has already consumed the record from storage.
 */
export async function resumeStepUpRequest(
  record: PendingStepUpResume,
): Promise<{ completed: StepUpResult; outcome?: StepUpResumeOutcome }> {
  const completed = await completeStepUpChallenge(record.challengeId, record.nonce);
  if (!completed.ok) return { completed };
  if (!isStepUpResumeActionId(record.action)) return { completed, outcome: { ok: false } };
  return { completed, outcome: await STEP_UP_RESUME_ACTIONS[record.action](record.payload, record.idempotencyKey) };
}

export async function fetchAdminGroups(): Promise<{ groups: AdminGroup[]; error?: string }> {
  try {
    const res = await apiFetch("/v1/admin/groups", {}, "admin_groups");
    if (!res.ok) return { groups: [], error: `HTTP ${res.status}` };
    const data = await res.json();
    return { groups: data.groups || [] };
  } catch {
    return { groups: [], error: "Network error" };
  }
}

export async function fetchUserSessions(
  userId: string,
  signal?: AbortSignal,
): Promise<{
  sessions: AdminSession[];
  error?: string;
  reasonCode?: string;
  supportReference?: string;
  capability?: string;
}> {
  try {
    const res = await apiFetch(`/v1/admin/users/${encodeURIComponent(userId)}/sessions`, { signal }, "admin_user_sessions");
    if (!res.ok) {
      const body: unknown = await res.json().catch(() => undefined);
      const data = body && typeof body === "object" ? (body as Record<string, unknown>) : {};
      const support = responseSupport(res, data);
      const reasonCode = responseReasonCode(data);
      return {
        sessions: [],
        error: `HTTP ${res.status}`,
        ...(reasonCode ? { reasonCode } : {}),
        ...(support.supportReference ? { supportReference: support.supportReference } : {}),
        ...(support.capability ? { capability: support.capability } : {}),
      };
    }
    const data = await res.json();
    return { sessions: Array.isArray(data.sessions) ? data.sessions.map(normalizeAdminSession) : [] };
  } catch {
    return { sessions: [], error: "Network error" };
  }
}

export async function fetchUserLoginHistory(
  userId: string,
  signal?: AbortSignal,
): Promise<{
  events: AuditEvent[];
  error?: string;
  reasonCode?: string;
  supportReference?: string;
  capability?: string;
}> {
  try {
    const res = await apiFetch(`/v1/admin/users/${encodeURIComponent(userId)}/login-history`, { signal }, "admin_user_login_history");
    if (!res.ok) {
      const body: unknown = await res.json().catch(() => undefined);
      const data = body && typeof body === "object" ? (body as Record<string, unknown>) : {};
      const support = responseSupport(res, data);
      const reasonCode = responseReasonCode(data);
      return {
        events: [],
        error: `HTTP ${res.status}`,
        ...(reasonCode ? { reasonCode } : {}),
        ...(support.supportReference ? { supportReference: support.supportReference } : {}),
        ...(support.capability ? { capability: support.capability } : {}),
      };
    }
    const data = await res.json();
    return { events: Array.isArray(data.events) ? data.events.map(normalizeAdminEvent) : [] };
  } catch {
    return { events: [], error: "Network error" };
  }
}

// --- Admin Roles ---

export interface AdminRoleDefinition {
  roleDefinitionId: string;
  organizationId: string;
  tenantId: string;
  environment: string;
  trustDomain: string;
  roleName: string;
  displayName: string;
  status: string;
  scopes: string[];
  permissions: string[];
}

export interface AdminRolesResult extends ResponseSupport {
  roles: AdminRoleDefinition[];
  error?: string;
  reasonCode?: string;
  httpStatus?: number;
}

export async function fetchAdminRoles(): Promise<AdminRolesResult> {
  try {
    const res = await apiFetch("/v1/admin/roles", {}, "admin_roles");
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      return {
        roles: [],
        error: `HTTP ${res.status}`,
        reasonCode: responseReasonCode((data ?? {}) as Record<string, unknown>),
        httpStatus: res.status,
        ...responseSupport(res, data),
      };
    }
    const data = await res.json();
    return { roles: data.roles || [] };
  } catch {
    return { roles: [], error: "Network error" };
  }
}

/* ── Developer / webhooks (ALL-4739) ──────────────────────────────
   Backend: WebhookController / WebhookRegistryService in
   alloy-api-gateway-service. Scope WEBHOOKS_MANAGE (+ permission
   webhook.provider.ingest); create/delete/test require step-up. The
   response never carries a raw signing secret, and the request body
   has no organization/tenant/environment/trust-domain field — every
   registration is stamped from the authenticated principal only. */

export interface WebhookListResult {
  endpoints: WebhookEndpoint[];
  nextPageToken?: string | null;
  error?: string;
  denied?: boolean;
}

export async function fetchWebhookEndpoints(): Promise<WebhookListResult> {
  try {
    const res = await apiFetch("/v1/webhooks", {}, "webhooks_list");
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const denied = res.status === 401 || res.status === 403;
      return { endpoints: [], error: (data as Record<string, string>)?.message || `HTTP ${res.status}`, denied };
    }
    return {
      endpoints: Array.isArray((data as Record<string, unknown>).items) ? (data as { items: WebhookEndpoint[] }).items : [],
      nextPageToken: (data as Record<string, unknown>).nextPageToken as string | null | undefined,
    };
  } catch {
    return { endpoints: [], error: "Network error" };
  }
}

export async function createWebhook(input: {
  url: string;
  eventTypes: string[];
}): Promise<{ ok: boolean; endpoint?: WebhookEndpoint; error?: string }> {
  try {
    const res = await apiFetch("/v1/webhooks", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // No secretRef: the server always issues its own secret-manager://
      // reference when none is supplied, and the UI never collects a raw
      // secret value to send.
      body: JSON.stringify({ url: input.url, eventTypes: input.eventTypes, secretRef: null }),
    }, "webhook_create");
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { ok: false, error: (data as Record<string, string>)?.message || (data as Record<string, string>)?.reasonCode || `HTTP ${res.status}` };
    }
    return { ok: true, endpoint: data as WebhookEndpoint };
  } catch {
    return { ok: false, error: "Network error" };
  }
}

export async function deleteWebhook(id: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const res = await apiFetch(`/v1/webhooks/${encodeURIComponent(id)}`, { method: "DELETE" }, "webhook_delete");
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      return { ok: false, error: (data as Record<string, string>)?.message || `HTTP ${res.status}` };
    }
    return { ok: true };
  } catch {
    return { ok: false, error: "Network error" };
  }
}

export interface WebhookTestDeliveryResult {
  ok: boolean;
  webhookId?: string;
  testDeliveryId?: string;
  status?: string;
  reasonCode?: string;
  evidenceRef?: string;
  error?: string;
}

export async function testWebhook(id: string): Promise<WebhookTestDeliveryResult> {
  try {
    const res = await apiFetch(`/v1/webhooks/${encodeURIComponent(id)}/test`, { method: "POST" }, "webhook_test");
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { ok: false, error: (data as Record<string, string>)?.message || `HTTP ${res.status}` };
    }
    return {
      ok: true,
      webhookId: data.webhookId,
      testDeliveryId: data.testDeliveryId,
      status: data.status,
      reasonCode: data.reasonCode,
      evidenceRef: data.evidenceRef,
    };
  } catch {
    return { ok: false, error: "Network error" };
  }
}

export interface WebhookDeliveryListResult {
  deliveries: WebhookDelivery[];
  error?: string;
}

export async function fetchWebhookDeliveries(webhookId: string): Promise<WebhookDeliveryListResult> {
  try {
    const res = await apiFetch(`/v1/webhooks/${encodeURIComponent(webhookId)}/deliveries`, {}, "webhook_deliveries");
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { deliveries: [], error: (data as Record<string, string>)?.message || `HTTP ${res.status}` };
    }
    return { deliveries: Array.isArray(data.items) ? data.items : [] };
  } catch {
    return { deliveries: [], error: "Network error" };
  }
}

// --- Hot wallets (ALL-5039 / SPEC-HW-8, HW-L9a) ---
//
// Typed clients for the gateway routes SPEC-HW-8 §2 names. None of these
// routes exist in any environment yet (HW-L1/L4/L7/L8 land the service, the
// deposit/sweep pipeline, ReconFlow's hot ledger, and the gateway routes in
// later lanes) — every call here goes through the same fetchList/fetchDetail
// primitives every other real page uses, so a call today returns a genuine
// unavailable/error response (typically a 404 from the gateway, since the
// route isn't registered) with a real D636 supportReference when the gateway
// supplies one, and once HW-L8 lands the same client and the same page start
// rendering real rows with no further change. Never mock the shape of a
// success response — an empty state must come from a length-0 items array,
// not a fabricated placeholder.

export interface ApiHotWallet {
  walletId: string;
  label?: string;
  chain?: string;
  status?: string;
  operatingBalance?: { asset: string; amount: string } | null;
  depositAddressCount?: number;
  pendingCreditAmount?: { asset: string; amount: string } | null;
  hotShareOfTotalPct?: number;
  hotShareCapPct?: number;
  coSignerStatus?: string;
  [key: string]: unknown;
}

export function fetchHotWallets(signal?: AbortSignal): Promise<ApiListResponse<ApiHotWallet>> {
  return fetchList<ApiHotWallet>("/v1/hot-wallets", "items", "hot_wallets_list", signal);
}

export function fetchHotWalletDetail(walletId: string): Promise<ApiDetailResponse<ApiHotWallet>> {
  return fetchDetail<ApiHotWallet>(`/v1/hot-wallets/${encodeURIComponent(walletId)}`, "hot_wallet_detail");
}

export interface ApiHotWalletDeposit {
  depositId: string;
  externalCustomerId?: string | null;
  address?: string;
  asset?: string;
  amount?: string;
  status?: string;
  confirmations?: string;
  detectedAt?: string;
  [key: string]: unknown;
}

export function fetchHotWalletDeposits(
  walletId: string,
  signal?: AbortSignal,
): Promise<ApiListResponse<ApiHotWalletDeposit>> {
  return fetchList<ApiHotWalletDeposit>(
    `/v1/hot-wallets/${encodeURIComponent(walletId)}/deposits`,
    "items",
    "hot_wallet_deposits",
    signal,
  );
}

export interface ApiHotWalletReconSummary {
  businessDate?: string;
  statementStatus?: "provisional" | "final" | string;
  identityHolds?: boolean;
  openExceptionCount?: number;
  proofRoot?: string | null;
  [key: string]: unknown;
}

export function fetchHotWalletReconciliationSummary(
  walletId: string,
): Promise<ApiDetailResponse<ApiHotWalletReconSummary>> {
  return fetchDetail<ApiHotWalletReconSummary>(
    `/v1/hot-wallets/${encodeURIComponent(walletId)}/recon/summary`,
    "hot_wallet_recon_summary",
  );
}

export interface ApiHotWalletPolicyProposal {
  proposalId: string;
  fromVersion?: string;
  toVersion?: string;
  summary?: string;
  status?: string;
  [key: string]: unknown;
}

export function fetchHotWalletPolicyProposals(
  signal?: AbortSignal,
): Promise<ApiListResponse<ApiHotWalletPolicyProposal>> {
  return fetchList<ApiHotWalletPolicyProposal>(
    "/v1/hot-wallets/policy-proposals",
    "items",
    "hot_wallet_policy_proposals",
    signal,
  );
}

// --- Enterprise SSO: domain verification (ALL-4870) ------------------------
//
// Contract: alloy-api-gateway-service/openapi/sso-admin-v1.yaml, the three
// operations marked `x-alloy-status: served` — `listSsoDomains`,
// `createSsoDomainChallenge` and `verifySsoDomainChallenge` (SPEC-SSO-3 §2,
// SPEC-SSO-1 §1). Everything else in that document is `planned` and is not
// called from here.
//
// The organization, tenant, environment and trust domain come from the
// authenticated principal; the client never sends them. Reads and writes are
// gated on the scope `identity_providers:manage` (AuthScope.
// IDENTITY_PROVIDERS_MANAGE -> Permission.AUTH_IDENTITY_PROVIDER_MANAGE,
// mapped to "sso:manage" in lib/permissions.ts), and the two writes also
// require a fresh AAL2 step-up, which the caller drives with
// withStepUpRetry. The contract declares no Idempotency-Key for these routes,
// so none is sent; the server refuses a duplicate claim itself
// (SSO_DOMAIN_CLAIMED / SSO_DOMAIN_ALREADY_VERIFIED).

export type SsoChallengeStatus = "PENDING" | "VERIFIED" | "EXPIRED" | "SUPERSEDED";

/** `ChallengeResponse` — `recordValue` carries the raw token on create only
 * and is null on every later response. */
export interface ApiSsoDomainChallenge {
  challengeId: string;
  domain: string;
  status: SsoChallengeStatus | string;
  recordName: string;
  recordValue?: string | null;
  expiresAt: string;
  firstConfirmedAt?: string | null;
  verifiedAt?: string | null;
  supportReference: string;
}

export interface SsoDomainListResult extends ResponseSupport {
  domains: ApiSsoDomainChallenge[];
  error?: ApiErrorInfo;
}

/**
 * GET /v1/admin/sso/domains (operationId `listSsoDomains`). The 200 body is a
 * bare JSON array (`ChallengeList`), which fetchList already unwraps.
 *
 * A transport failure is reported as NETWORK_ERROR rather than left to the
 * caller's generic catch, so the page can render its explicit
 * "not available in this environment" state instead of an empty table that
 * would read as "this organization has no domains".
 */
export async function fetchSsoDomains(signal?: AbortSignal): Promise<SsoDomainListResult> {
  try {
    const res = await fetchList<ApiSsoDomainChallenge>(
      "/v1/admin/sso/domains",
      "items",
      "sso_domains_list",
      signal,
    );
    return {
      domains: res.items,
      error: res.error,
      supportReference: res.error?.supportReference,
      capability: res.error?.capability,
    };
  } catch {
    return { domains: [], error: { reasonCode: "NETWORK_ERROR" } };
  }
}

export interface SsoDomainMutationResult extends ResponseSupport {
  ok: boolean;
  /** Server reason code (RFC 9457 `reasonCode`), e.g. SSO_DOMAIN_CLAIMED or
   * AUTH_STEP_UP_REQUIRED. Drives withStepUpRetry. */
  reasonCode?: string;
  error?: string;
  httpStatus?: number;
  /** The challenge as the API returned it. `recordValue` is present on create
   * only, never on verify. */
  domain?: ApiSsoDomainChallenge;
}

async function ssoDomainRequest(
  path: string,
  body: Record<string, unknown> | null,
  scope: string,
): Promise<SsoDomainMutationResult> {
  try {
    const res = await apiFetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: body === null ? undefined : JSON.stringify(body),
    }, scope);
    const data = await res.json().catch(() => ({}));
    const envelope = data && typeof data === "object" && !Array.isArray(data)
      ? data as Record<string, unknown>
      : {};
    if (!res.ok) {
      const info = extractApiError(res, envelope);
      return {
        ok: false,
        reasonCode: info.reasonCode,
        error: boundedErrorText(envelope.detail) || boundedErrorText(envelope.title) || info.reasonCode,
        httpStatus: res.status,
        ...responseSupport(res, data),
      };
    }
    return {
      ok: true,
      domain: envelope as unknown as ApiSsoDomainChallenge,
      httpStatus: res.status,
      ...responseSupport(res, data),
    };
  } catch {
    return { ok: false, reasonCode: "NETWORK_ERROR", error: "Network error" };
  }
}

/**
 * POST /v1/admin/sso/domains (operationId `createSsoDomainChallenge`,
 * SPEC-SSO-1 §1.1). Body is exactly `{domain}` — `DomainCreateBody` declares
 * `additionalProperties: false`. Requires a human session with a fresh AAL2
 * step-up; wrap in withStepUpRetry.
 */
export function createSsoDomainChallenge(domain: string): Promise<SsoDomainMutationResult> {
  return ssoDomainRequest("/v1/admin/sso/domains", { domain }, "sso_domain_create");
}

/**
 * POST /v1/admin/sso/domains/{challengeId}/verify (operationId
 * `verifySsoDomainChallenge`, SPEC-SSO-1 §1.2). The contract declares no
 * request body. Requires a human session with a fresh AAL2 step-up; wrap in
 * withStepUpRetry.
 */
export function verifySsoDomainChallenge(challengeId: string): Promise<SsoDomainMutationResult> {
  return ssoDomainRequest(
    `/v1/admin/sso/domains/${encodeURIComponent(challengeId)}/verify`,
    null,
    "sso_domain_verify",
  );
}

// ── Provider migration (Fireblocks, read-only) — ALL-5444 / gateway contract ALL-5445 ──
// GET-only. The gateway calls the adapter's internal source-inventory surface server-side; the
// browser never sees the adapter token, credentials, or the raw provider attribute map.

export type ProviderMigrationCategory = "VAULT_ACCOUNTS" | "ASSET_WALLETS" | "VAULT_ADDRESSES";

export interface ApiProviderMigrationConnection {
  provider: string;
  workspaceId: string;
  environment: string;
  /** A secret NAME/reference, never a credential value. */
  secretReferenceName: string;
  healthy: boolean;
  snapshotId?: string | null;
  lastCheckedAt?: string | null;
}

export interface ApiProviderMigrationInventoryItem {
  resourceType: string;
  providerResourceId: string;
  /**
   * Gateway-allow-listed provider attributes, all exact strings (ALL-5704; additive, so older
   * responses omit it). VAULT_ACCOUNTS: name, hiddenOnUi. ASSET_WALLETS: vaultAccountId, assetId,
   * total, available, pending, frozen, lockedAmount, staked (decimal strings, never parsed to a
   * float). VAULT_ADDRESSES: vaultAccountId, assetId, address, addressFormat, type. Any key may be absent.
   */
  attributes?: Record<string, unknown> | null;
}

export interface ApiProviderMigrationInventoryPage {
  snapshotId: string;
  category: string;
  items: ApiProviderMigrationInventoryItem[];
  nextCursor?: string | null;
  freshUntil?: string | null;
}

/**
 * fetchDetail, but tolerant of a non-JSON error body: a proxy 502/503/504 or a 404 from an
 * un-deployed route can answer with HTML or nothing. That must still render the honest
 * not-available state with the support reference from the response header, never throw a JSON
 * parse error into the page.
 */
async function fetchProviderMigrationDetail<T>(path: string, scope: string): Promise<ApiDetailResponse<T>> {
  const { response: res } = await apiFetchWithCorrelation(path, {}, scope);
  const data: unknown = await res.json().catch(() => null);
  const body = data && typeof data === "object" ? (data as Record<string, unknown>) : {};
  if (!res.ok) return { item: null, error: extractApiError(res, body) };
  if (!data || typeof data !== "object") {
    return { item: null, error: { reasonCode: "INVALID_RESPONSE", ...responseSupport(res, body) } };
  }
  return { item: data as T };
}

export function fetchProviderMigrationConnection(): Promise<ApiDetailResponse<ApiProviderMigrationConnection>> {
  return fetchProviderMigrationDetail<ApiProviderMigrationConnection>(
    "/v1/reconciliation/provider-migration/connections/current",
    "provider_migration_connection",
  );
}

export function fetchProviderMigrationInventoryPage(
  category: ProviderMigrationCategory,
  limit: number,
  cursor?: string | null,
): Promise<ApiDetailResponse<ApiProviderMigrationInventoryPage>> {
  const query = new URLSearchParams({ category, limit: String(limit) });
  if (cursor) query.set("cursor", cursor);
  return fetchProviderMigrationDetail<ApiProviderMigrationInventoryPage>(
    `/v1/reconciliation/provider-migration/inventory?${query.toString()}`,
    "provider_migration_inventory",
  );
}

// --- Enterprise SSO: identity providers, role mapping, enable/disable ------
//
// Contract: the frozen wire contract (sso-contract.md) for this slice —
// `getSsoOverview`, `createOidcProvider`, `updateProviderRoles`,
// `enableProvider`, `disableProvider`, `getBreakGlassAdmins` and
// `updateBreakGlassAdmins`. Every route needs a human browser session with
// `identity_providers:manage`; an API key or agent key gets 403. The
// organization always comes from the session, so no body here carries an
// organization or tenant id, and no request carries an Idempotency-Key the
// contract does not declare.
//
// The client secret is write-only: it is sent on create and never appears in
// any response, so there is no reader function for it and nothing in this
// module can render one.

export type SsoProviderTemplate = "entra" | "okta" | "keycloak" | "ping" | "generic";

/** The templates the contract serves for organization-created providers.
 * `google-workspace-oidc` is deliberately absent: the server refuses it with
 * SSO_TEMPLATE_REFUSED, so offering it would be offering a guaranteed failure. */
export const SSO_PROVIDER_TEMPLATES: SsoProviderTemplate[] = [
  "entra",
  "okta",
  "keycloak",
  "ping",
  "generic",
];

export interface ApiSsoProviderRoute {
  routeId: string;
  domain: string;
  status: string;
  enforcement: string;
}

export interface ApiSsoRoleMapping {
  group: string;
  roleName: string;
}

/** `ProviderDetail`. `clientId` is null on deployment-configured rows, and the
 * client secret itself is never present — only when it was set and its
 * fingerprint. */
export interface ApiSsoProvider {
  identityProviderId: string;
  displayName: string;
  template: string;
  providerType: string;
  status: string;
  routeHandle: string;
  issuerOrEntityId: string;
  clientId?: string | null;
  clientSecretSetAt?: string | null;
  clientSecretFingerprint?: string | null;
  domains: string[];
  redirectUri: string;
  bookmarkUrl: string;
  roleMappingMode: string;
  roleMappings: ApiSsoRoleMapping[];
  routes: ApiSsoProviderRoute[];
  /** ADMIN_API rows are editable here; DEPLOYMENT_CONFIG and ORG_FACTORY rows
   * are read-only on this page. */
  createdVia: string;
  configurationVersion?: number | string;
  supportReference?: string;
}

/** Rows the admin API did not create are read-only on this page. */
export function isAdminApiProvider(provider: ApiSsoProvider): boolean {
  return provider.createdVia === "ADMIN_API";
}

export interface ApiSsoBreakGlassSummary {
  minimum: number;
  /** How many designated admins are live (signed in within 90 days via the platform path). */
  live: number;
  satisfied: boolean;
}

export interface ApiSsoOverview {
  providers: ApiSsoProvider[];
  breakGlass: ApiSsoBreakGlassSummary | null;
}

export interface SsoOverviewResult extends ResponseSupport {
  providers: ApiSsoProvider[];
  breakGlass: ApiSsoBreakGlassSummary | null;
  error?: ApiErrorInfo;
}

/**
 * GET /v1/admin/sso (operationId `getSsoOverview`). The organization scope
 * comes from the session. A transport failure is reported as NETWORK_ERROR so
 * the page renders its explicit "not available here" state rather than an
 * empty list that would read as "this organization has no identity provider".
 */
export async function fetchSsoOverview(signal?: AbortSignal): Promise<SsoOverviewResult> {
  try {
    const res = await apiFetch("/v1/admin/sso", { signal }, "sso_overview");
    const data = await res.json().catch(() => ({}));
    const body = data && typeof data === "object" && !Array.isArray(data)
      ? data as Record<string, unknown>
      : {};
    if (!res.ok) {
      return { providers: [], breakGlass: null, error: extractApiError(res, body) };
    }
    return {
      providers: Array.isArray(body.providers) ? body.providers as ApiSsoProvider[] : [],
      breakGlass: body.breakGlass && typeof body.breakGlass === "object"
        ? body.breakGlass as ApiSsoBreakGlassSummary
        : null,
      supportReference: boundedErrorText(body.supportReference),
    };
  } catch {
    return { providers: [], breakGlass: null, error: { reasonCode: "NETWORK_ERROR" } };
  }
}

interface SsoMutationEnvelope extends ResponseSupport {
  ok: boolean;
  reasonCode?: string;
  error?: string;
  httpStatus?: number;
  body: Record<string, unknown>;
}

/** Shared JSON write path for the SSO admin routes: RFC 9457 refusals keep
 * their reasonCode and the server's support reference (D636), and a transport
 * failure becomes NETWORK_ERROR rather than an unhandled throw. */
async function ssoMutationRequest(
  method: "POST" | "PUT",
  path: string,
  body: unknown,
  scope: string,
): Promise<SsoMutationEnvelope> {
  try {
    const res = await apiFetch(path, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }, scope);
    const data = await res.json().catch(() => ({}));
    const envelope = data && typeof data === "object" && !Array.isArray(data)
      ? data as Record<string, unknown>
      : {};
    if (!res.ok) {
      const info = extractApiError(res, envelope);
      return {
        ok: false,
        reasonCode: info.reasonCode,
        error: boundedErrorText(envelope.detail) || boundedErrorText(envelope.title) || info.reasonCode,
        httpStatus: res.status,
        body: envelope,
        ...responseSupport(res, data),
      };
    }
    return { ok: true, httpStatus: res.status, body: envelope, ...responseSupport(res, data) };
  } catch {
    return { ok: false, reasonCode: "NETWORK_ERROR", error: "Network error", body: {} };
  }
}

export interface SsoProviderMutationResult extends ResponseSupport {
  ok: boolean;
  reasonCode?: string;
  error?: string;
  httpStatus?: number;
  /** `ProviderDetail` as the API returned it. */
  provider?: ApiSsoProvider;
}

async function ssoProviderMutation(
  method: "POST" | "PUT",
  path: string,
  body: unknown,
  scope: string,
): Promise<SsoProviderMutationResult> {
  const result = await ssoMutationRequest(method, path, body, scope);
  return {
    ok: result.ok,
    reasonCode: result.reasonCode,
    error: result.error,
    httpStatus: result.httpStatus,
    provider: result.ok ? (result.body as unknown as ApiSsoProvider) : undefined,
    supportReference: result.supportReference,
    capability: result.capability,
  };
}

/** `OidcProviderCreateBody`. In this slice issuer, clientId, clientSecret and
 * at least one verified domain are required; `additionalProperties: false`
 * means these six fields and nothing else. */
export interface OidcProviderCreateInput {
  displayName: string;
  template: SsoProviderTemplate;
  issuer: string;
  clientId: string;
  clientSecret: string;
  domains: string[];
}

/**
 * POST /v1/admin/sso/providers/oidc (operationId `createOidcProvider`). Creates
 * a DRAFT provider; the client secret is written once and never returned.
 * Requires a human session with a fresh AAL2 step-up; wrap in withStepUpRetry.
 */
export function createSsoOidcProvider(input: OidcProviderCreateInput): Promise<SsoProviderMutationResult> {
  return ssoProviderMutation("POST", "/v1/admin/sso/providers/oidc", input, "sso_provider_create");
}

/**
 * PUT /v1/admin/sso/providers/{identityProviderId}/roles (operationId
 * `updateProviderRoles`). `EXPLICIT_ONLY` requires an empty `mappings` array;
 * a privileged role (admin, approval, key or invitation scopes) is refused with
 * 422 SSO_ROLE_PRIVILEGED. Requires a fresh AAL2 step-up.
 */
export function updateSsoProviderRoles(
  identityProviderId: string,
  mode: "EXPLICIT_ONLY" | "MAP_GROUPS",
  mappings: ApiSsoRoleMapping[],
): Promise<SsoProviderMutationResult> {
  return ssoProviderMutation(
    "PUT",
    `/v1/admin/sso/providers/${encodeURIComponent(identityProviderId)}/roles`,
    { mode, mappings },
    "sso_provider_roles",
  );
}

/**
 * POST /v1/admin/sso/providers/{identityProviderId}/enable (operationId
 * `enableProvider`). Every domain must be verified and unclaimed, and the
 * organization needs a live break-glass administrator (422
 * SSO_BREAK_GLASS_REQUIRED). Requires a fresh AAL2 step-up.
 */
export function enableSsoProvider(
  identityProviderId: string,
  domains: string[],
): Promise<SsoProviderMutationResult> {
  return ssoProviderMutation(
    "POST",
    `/v1/admin/sso/providers/${encodeURIComponent(identityProviderId)}/enable`,
    { domains },
    "sso_provider_enable",
  );
}

/**
 * POST /v1/admin/sso/providers/{identityProviderId}/disable (operationId
 * `disableProvider`). The reason is required by the contract and is recorded
 * with the change. Requires a fresh AAL2 step-up.
 */
export function disableSsoProvider(
  identityProviderId: string,
  reason: string,
): Promise<SsoProviderMutationResult> {
  return ssoProviderMutation(
    "POST",
    `/v1/admin/sso/providers/${encodeURIComponent(identityProviderId)}/disable`,
    { reason },
    "sso_provider_disable",
  );
}

/** `admins[]` of `BreakGlassList`: every ACTIVE human organization_admin, with
 * `live` meaning "is break-glass and signed in within 90 days through a
 * non-ADMIN_API provider". */
export interface ApiSsoBreakGlassAdmin {
  principalId: string;
  email: string;
  displayName: string;
  breakGlass: boolean;
  live: boolean;
  lastSignInAt?: string | null;
}

export interface SsoBreakGlassResult extends ResponseSupport {
  admins: ApiSsoBreakGlassAdmin[];
  minimum: number;
  error?: ApiErrorInfo;
}

/**
 * GET /v1/admin/sso/break-glass (operationId `getBreakGlassAdmins`). The list
 * is every eligible administrator, so a checkbox that is unchecked is a real
 * "not designated", not a missing row.
 */
export async function fetchSsoBreakGlass(signal?: AbortSignal): Promise<SsoBreakGlassResult> {
  try {
    const res = await apiFetch("/v1/admin/sso/break-glass", { signal }, "sso_break_glass");
    const data = await res.json().catch(() => ({}));
    const body = data && typeof data === "object" && !Array.isArray(data)
      ? data as Record<string, unknown>
      : {};
    if (!res.ok) return { admins: [], minimum: 0, error: extractApiError(res, body) };
    return {
      admins: Array.isArray(body.admins) ? body.admins as ApiSsoBreakGlassAdmin[] : [],
      minimum: typeof body.minimum === "number" ? body.minimum : 0,
      supportReference: boundedErrorText(body.supportReference),
    };
  } catch {
    return { admins: [], minimum: 0, error: { reasonCode: "NETWORK_ERROR" } };
  }
}

export interface SsoBreakGlassMutationResult extends ResponseSupport {
  ok: boolean;
  reasonCode?: string;
  error?: string;
  httpStatus?: number;
  admins?: ApiSsoBreakGlassAdmin[];
  minimum?: number;
}

/**
 * PUT /v1/admin/sso/break-glass (operationId `updateBreakGlassAdmins`). The
 * contract replaces the whole set, so `principalIds` is the complete desired
 * list. Removing the last break-glass administrator while a customer provider
 * is active is refused with 409 SSO_LAST_BREAK_GLASS. Requires a fresh AAL2
 * step-up.
 */
export async function updateSsoBreakGlass(principalIds: string[]): Promise<SsoBreakGlassMutationResult> {
  const result = await ssoMutationRequest(
    "PUT",
    "/v1/admin/sso/break-glass",
    { principalIds },
    "sso_break_glass_update",
  );
  return {
    ok: result.ok,
    reasonCode: result.reasonCode,
    error: result.error,
    httpStatus: result.httpStatus,
    admins: Array.isArray(result.body.admins) ? result.body.admins as ApiSsoBreakGlassAdmin[] : undefined,
    minimum: typeof result.body.minimum === "number" ? result.body.minimum : undefined,
    supportReference: result.supportReference,
    capability: result.capability,
  };
}
